import { Check, Copy, Menu, Monitor, Moon, Sun, X } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import shortcutData from "./shortcuts.json";

import "./index.css";

import pons from "./assets/pons.png";
import pump from "./assets/pump.png";
import robinhood from "./assets/robinhood.png";
import solana from "./assets/solana.png";
import uniswap from "./assets/uniswap.png";
import flaunch from "./assets/flaunch.png";
import poolsTrade from "./assets/pools-trade.svg";
import poolsFun from "./assets/pools-fun.svg";
import { LedTicker } from "./LedTicker";
import { applyTheme, byId, clearTheme, DEFAULT_THEME, restoreTheme, saveTheme, savedThemeId, THEMES } from "./themes";

/**
 * Fades and lifts its children the first time they scroll into view.
 *
 * Once only — content that re-animates every time it passes the viewport turns
 * a scroll back up into a light show.
 */
/**
 * Text that rises into place, a character at a time, from under its own line.
 *
 * Written against the DOM React already owns rather than with a splitter
 * library: every one of those works by tearing the text node apart and
 * rebuilding it as spans, which is fine for a static heading and fatal here —
 * the hero paragraph contains live `<TradeKey>` keycaps you can press, and a
 * library that rewrites their nodes would leave React holding references to
 * elements that are no longer in the tree.
 *
 * So: string children are split here, in React. Anything that is NOT a string
 * — a keycap, a link — passes through whole and animates as one unit, which is
 * also what you want, since a keycap is a single object rather than a word.
 *
 * The stagger runs across the whole block, so a heading reads as one motion
 * rather than each word starting over.
 */
function SplitText({
  children,
  className = "",
  delay = 0,
  /** Milliseconds between one character and the next. */
  step = 18,
  /** The full string, for anything that reads rather than looks. */
  label,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  step?: number;
  label: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // One counter across every segment, so the stagger does not restart at each
  // coloured span or keycap.
  let n = 0;
  const stamp = () => ({ "--d": `${delay + n++ * step}ms` }) as React.CSSProperties;

  const render = (node: React.ReactNode, key: React.Key): React.ReactNode => {
    if (typeof node === "number") node = String(node);
    // A plain wrapper around text — a coloured span, a forced line — is
    // recursed into rather than swallowed: its class is what makes the second
    // half of the heading brand-coloured, and treating it as one atom would
    // rise those four words as a single lump instead of letter by letter.
    //
    // Restricted to intrinsic tags (`<span>`, `<em>`) by the `typeof type ===
    // "string"` check. A custom component must never be rebuilt this way: its
    // children are its own contract, and handing it a pile of per-character
    // spans is not something it agreed to render.
    if (
      React.isValidElement(node) &&
      typeof node.type === "string" &&
      (typeof node.props.children === "string" || Array.isArray(node.props.children))
    ) {
      return React.cloneElement(
        node as React.ReactElement<{ children?: React.ReactNode }>,
        { key },
        React.Children.map(node.props.children, (c, i) => render(c, i)),
      );
    }
    if (typeof node !== "string") {
      // Not text — animate it whole. The word wrapper still masks it, so a
      // keycap rises out of the line like everything around it.
      return (
        <span key={key} className="split-word">
          <span className="split-char" style={stamp()}>
            {node}
          </span>
        </span>
      );
    }
    // `split(/(\s+)/)` keeps the whitespace, so the gaps between words survive
    // and the text still wraps where it always did.
    return node.split(/(\s+)/).map((chunk, ci) => {
      if (!chunk) return null;
      if (/^\s+$/.test(chunk)) return <span key={`${key}-${ci}`}>{chunk}</span>;
      return (
        <span key={`${key}-${ci}`} className="split-word">
          {[...chunk].map((ch, i) => (
            <span key={i} className="split-char" style={stamp()}>
              {ch}
            </span>
          ))}
        </span>
      );
    });
  };

  return (
    <span
      ref={ref}
      data-shown={shown}
      className={className}
      // The pieces are decoration; the label is the sentence.
      aria-label={label}
    >
      <span aria-hidden="true">
        {React.Children.map(children, (child, i) => render(child, i))}
      </span>
    </span>
  );
}

function Reveal({
  children,
  delay = 0,
  className = "",
}: {
  children: React.ReactNode;
  /** Milliseconds, for staggering a group. */
  delay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      // A little inset so it fires as the element clears the fold, not the
      // instant its first pixel appears.
      { rootMargin: "0px 0px -10% 0px", threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-shown={shown}
      style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`reveal ${className}`}
    >
      {children}
    </div>
  );
}

/**
 * A keycap you can press — with the mouse, or by pressing the key itself. It
 * does what the app does: sends the order, which lands on the tape behind it
 * marked as yours.
 */
function TradeKey({ k, side }: { k: string; side: "buy" | "sell" }) {
  const [down, setDown] = useState(false);

  const fire = () =>
    window.dispatchEvent(new CustomEvent("trenches:trade", { detail: side }));

  useEffect(() => {
    const typing = () => {
      const el = document.activeElement;
      return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.key !== k || e.metaKey || e.ctrlKey || e.altKey || typing()) return;
      // The cap stays down for as long as the key is held, like the real one.
      setDown(true);
      // Auto-repeat holds the key down; it should not fire a hundred orders.
      if (!e.repeat) fire();
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key === k) setDown(false);
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    // A window that loses focus mid-hold never sees the keyup.
    window.addEventListener("blur", () => setDown(false));
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [k, side]);

  return (
    <kbd
      role="button"
      tabIndex={0}
      aria-label={`Press ${k} to send a ${side}`}
      data-down={down || undefined}
      onPointerDown={() => {
        setDown(true);
        fire();
      }}
      onPointerUp={() => setDown(false)}
      onPointerLeave={() => setDown(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          setDown(true);
          fire();
        }
      }}
      onKeyUp={() => setDown(false)}
      className="keycap cursor-pointer"
    >
      {k}
    </kbd>
  );
}

/**
 * A shell command, shown the way it looks in the terminal — with a copy button,
 * because a command nobody can copy is a command they will mistype.
 */
function Cmd({ children, tbd }: { children: string; tbd?: boolean }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard is permission-gated and can simply refuse; the command is
      // still selectable by hand, so failing quietly is the right outcome.
    }
  };

  return (
    <code className="flex items-center gap-2.5 rounded border border-border bg-card px-3.5 py-2.5 text-[0.9rem]">
      <span className="shrink-0 text-brand">$</span>
      <span className="no-scrollbar min-w-0 flex-1 overflow-x-auto whitespace-nowrap">{children}</span>

      {tbd ? (
        <span className="shrink-0 rounded-sm border border-border px-1.5 py-px text-[10px] tracking-wider text-muted-foreground uppercase">
          tbd
        </span>
      ) : (
        <button
          type="button"
          onClick={copy}
          aria-label={copied ? "Copied" : `Copy: ${children}`}
          className="shrink-0 text-muted-foreground transition-colors hover:text-brand"
        >
          {copied ? <Check className="h-4 w-4 text-good" /> : <Copy className="h-4 w-4" />}
        </button>
      )}
    </code>
  );
}

/** Chains and protocols the app trades, in the order you meet them. Entries
 *  marked `soon` are announced, not shipped — they read dimmed and say so, so
 *  the band never implies you can trade something you cannot. */
const PROTOCOLS: { name: string; src?: string; soon?: boolean }[] = [
  { name: "Uniswap V3", src: uniswap },
  { name: "Uniswap V4", src: uniswap },
  { name: "pons.family", src: pons },
  { name: "pump.fun", src: pump },
  { name: "PumpSwap AMM", src: pump },
  { name: "Flaunch", src: flaunch },
  { name: "pools.trade", src: poolsTrade },
  { name: "pools.fun", src: poolsFun },
  { name: "Bankr", soon: true },
  { name: "Noxa", soon: true },
  { name: "Long", soon: true },
  { name: "Bow", soon: true },
];

function MarqueeItem({
  name,
  src,
  soon,
  clone,
}: {
  name: string;
  src?: string;
  soon?: boolean;
  clone?: boolean;
}) {
  return (
    <span
      // The duplicate exists only to make the loop seamless, so it is hidden
      // from screen readers rather than read out twice.
      aria-hidden={clone || undefined}
      className={`flex shrink-0 items-center gap-2.5 border-r border-border px-5 py-2 ${
        soon ? "opacity-45" : ""
      }`}
    >
      {src && (
        <span className="flex h-[22px] shrink-0 items-center justify-center rounded-sm bg-foreground/5 px-1.5">
          <img src={src} alt={clone ? "" : name} className="block h-3.5 w-auto" />
        </span>
      )}
      <span className="whitespace-nowrap text-xs text-foreground">{name}</span>
      {soon && (
        <span className="rounded-sm border border-border px-1.5 py-px text-[10px] tracking-wider text-muted-foreground uppercase">
          soon
        </span>
      )}
    </span>
  );
}

/**
 * Drop a screenshot of the running TUI in here and it replaces the mock:
 *
 *   import shot from "./assets/screenshot.png";
 *   <TerminalPreview src={shot} />
 *
 * Until then the mock stands in, so the layout is already the right shape and
 * nothing has to be redesigned around the real image later.
 */
type Trade = {
  id: number;
  at: number;
  buy: boolean;
  eth: number;
  cap: number;
  fresh?: boolean;
  /** Sent from a keycap — the app marks your own fills the same way. */
  mine?: boolean;
};

/** Hard ceiling on the tape, by breakpoint. Rows are cheap, but each is a DOM
 *  node that re-renders every second for its age — and on a medium screen the
 *  panel is short enough that a long tape just reads as a wall. */
const MAX_ROWS = 40;
// Chosen to FIT the panel at each size, not to fill it. The panel has a fixed
// height and hides its overflow, so a row count that exceeds it cuts the window
// frame off mid-tape and the screenshot stops looking like a window.
const rowCap = () => (window.innerWidth >= 1280 ? MAX_ROWS : 8);

/** $1.07M / $912K — the way the app compacts a cap. */
const usd = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(2)}M` : `$${Math.round(n / 1000)}K`;

/** Market cap drifts with the flow: buys lift it, sells knock it back. */
let cap = 1_070_000;

const newTrade = (buy = Math.random() > 0.45, fresh = false): Trade => {
  const eth = Math.random() * 2.1 + 0.02;
  cap = Math.max(120_000, cap + eth * (buy ? 9_400 : -7_100));
  return { id: Math.random(), at: Date.now(), buy, eth, cap, fresh };
};

/**
 * The hero chart: a full-width candle simulation with a pulse. It runs a
 * little regime machine — grind, moonshot, dump — so the tape reads like a
 * memecoin and not like noise, and it is SEEDED full so the frame is never
 * half empty. The newest candle forms on the right, where the hero copy
 * doesn't cover it.
 */
const HERO_CANDLES = 34;

type HeroCandle = { o: number; h: number; l: number; c: number };

/** Roughly-normal noise — sums of uniforms, cheap and good enough. */
function gauss() {
  return Math.random() + Math.random() + Math.random() + Math.random() - 2;
}

/**
 * The walk is noise around an ANCHOR PATH. The anchor takes the big steps —
 * a moonshot candle moves it 5–12%, a dump 7–14% — and the close snaps
 * around it, so each body is a real fraction of what the frame can show.
 * Tuning tiny per-tick percents never got there: cumulative drift grew the
 * range and every candle shrank in proportion.
 */
function nextHeroCandle(prev: HeroCandle, regime: { mode: string; left: number; vol: number; anchor: number }): HeroCandle {
  const o = prev.c;
  const burst = Math.random() < 0.12 ? 1.8 + Math.random() * 2 : 1;
  const c = Math.max(0.15, regime.anchor * (1 + gauss() * 0.045 * regime.vol * burst));
  const top = Math.max(o, c);
  const bot = Math.min(o, c);
  // Wicks read as drama, but an unbounded wick DEFINES the price range and
  // squeezes every body into a thin band — capped near the body's own scale.
  const wickUp = Math.min(0.1, Math.pow(Math.random(), 3) * 0.09 * regime.vol * (Math.random() < 0.08 ? 2.5 : 1));
  const wickDn = Math.min(0.1, Math.pow(Math.random(), 3) * 0.09 * regime.vol * (Math.random() < 0.08 ? 2.5 : 1));
  return { o, h: top * (1 + wickUp), l: Math.max(0.1, bot * (1 - wickDn)), c };
}

/** One TICK inside the forming candle: the close chases the anchor with
 *  noise, carving the high and low it passes through on the way. */
function tickHeroCandle(cur: HeroCandle, regime: { mode: string; left: number; vol: number; anchor: number }): HeroCandle {
  const burst = Math.random() < 0.07 ? 2 + Math.random() * 2.5 : 1;
  const chase = (regime.anchor - cur.c) * (0.2 + Math.random() * 0.2);
  const c = Math.max(0.15, cur.c + chase + cur.c * gauss() * 0.02 * regime.vol * burst);
  return { o: cur.o, h: Math.max(cur.h, c), l: Math.min(cur.l, c), c };
}

function stepRegime(r: { mode: string; left: number; vol: number; anchor: number }) {
  // Volatility clusters: it drifts, it doesn't reset.
  const vol = Math.min(3.5, Math.max(0.5, r.vol * (0.85 + Math.random() * 0.4)));
  let mode = r.mode;
  let left = r.left - 1;
  if (left < 0) {
    const roll = Math.random();
    if (r.mode === "moon") {
      mode = roll < 0.45 ? "dump" : "grind";
      left = 2 + Math.floor(Math.random() * 5);
    } else if (roll < 0.22) {
      mode = "moon";
      left = 4 + Math.floor(Math.random() * 7);
    } else if (roll < 0.34) {
      mode = "dump";
      left = 2 + Math.floor(Math.random() * 4);
    } else {
      mode = "grind";
      left = 2 + Math.floor(Math.random() * 5);
    }
  }
  // The anchor takes the BIG step for this candle.
  const anchor =
    mode === "moon" ? r.anchor * (1.05 + Math.random() * 0.07) :
    mode === "dump" ? r.anchor * (0.86 + Math.random() * 0.07) :
    r.anchor * (0.985 + Math.random() * 0.03);
  return { mode, left, vol, anchor: Math.max(0.2, anchor) };
}

/**
 * The shapes every chart-reader already knows, as seed scripts. Real tapes
 * rhyme: the launch spike that M-tops and bleeds before a second wind; the
 * double top; the stair-climber making higher lows; the bleeder with its
 * dead-cat bounces; the range that finally breaks a level. Each leg is a
 * target multiple spread over n candles at a volatility — the noise machine
 * paints on top, so the same archetype never draws twice.
 */
type SeedLeg = { to: number; n: number; vol: number };

const ARCHETYPES: SeedLeg[][] = [
  // Token launch: vertical open, hard unwind, long quiet floor, second wind.
  [
    { to: 3.4, n: 5, vol: 2.2 },
    { to: 0.4, n: 4, vol: 2.4 },
    { to: 0.95, n: 13, vol: 0.6 },
    { to: 2.2, n: 8, vol: 1.7 },
    { to: 0.94, n: 4, vol: 1.0 },
  ],
  // The M: climb, dip, re-test the high, fail, bleed to a base.
  [
    { to: 2.3, n: 8, vol: 1.3 },
    { to: 0.74, n: 3, vol: 1.2 },
    { to: 1.34, n: 5, vol: 1.3 },
    { to: 0.52, n: 10, vol: 1.5 },
    { to: 1.02, n: 8, vol: 0.7 },
  ],
  // Stair-climber: higher highs, higher lows, all the way out.
  [
    { to: 1.4, n: 4, vol: 1.1 },
    { to: 0.9, n: 2, vol: 0.8 },
    { to: 1.4, n: 4, vol: 1.1 },
    { to: 0.88, n: 2, vol: 0.8 },
    { to: 1.45, n: 5, vol: 1.2 },
    { to: 0.92, n: 3, vol: 0.8 },
    { to: 1.5, n: 8, vol: 1.3 },
    { to: 0.96, n: 6, vol: 0.9 },
  ],
  // Bleeder: lower highs, lower lows, dead-cat bounces on the way down.
  [
    { to: 0.72, n: 4, vol: 1.3 },
    { to: 1.16, n: 2, vol: 1.0 },
    { to: 0.66, n: 5, vol: 1.4 },
    { to: 1.14, n: 3, vol: 1.0 },
    { to: 0.7, n: 6, vol: 1.3 },
    { to: 1.22, n: 4, vol: 1.1 },
    { to: 0.78, n: 6, vol: 1.2 },
    { to: 1.05, n: 4, vol: 0.8 },
  ],
  // Range, range, range — then the level finally gives.
  [
    { to: 1.09, n: 3, vol: 0.6 },
    { to: 0.92, n: 3, vol: 0.6 },
    { to: 1.08, n: 3, vol: 0.6 },
    { to: 0.93, n: 3, vol: 0.6 },
    { to: 1.07, n: 3, vol: 0.7 },
    { to: 0.94, n: 3, vol: 0.7 },
    { to: 1.9, n: 6, vol: 1.9 },
    { to: 0.97, n: 10, vol: 0.9 },
  ],
];

function seedHeroCandles(): HeroCandle[] {
  const legs = ARCHETYPES[Math.floor(Math.random() * ARCHETYPES.length)]!;
  const out: HeroCandle[] = [];
  let prev: HeroCandle = { o: 1, h: 1, l: 1, c: 1 };
  let anchor = 1;
  outer: for (const leg of legs) {
    // The leg's move, spread geometrically across its candles with jitter —
    // the archetype sets the road, the walk still owns the driving.
    const step = Math.pow(leg.to, 1 / leg.n);
    for (let k = 0; k < leg.n; k++) {
      anchor = Math.max(0.15, anchor * step * (1 + gauss() * 0.012));
      prev = nextHeroCandle(prev, { mode: "grind", left: 0, vol: leg.vol, anchor });
      out.push(prev);
      if (out.length >= HERO_CANDLES) break outer;
    }
  }
  // A short archetype pads out flat at its final level.
  while (out.length < HERO_CANDLES) {
    anchor = Math.max(0.15, anchor * (1 + gauss() * 0.015));
    prev = nextHeroCandle(prev, { mode: "grind", left: 0, vol: 0.7, anchor });
    out.push(prev);
  }
  return out;
}

/**
 * The simulation lives OUTSIDE the component: flipping tape/chart unmounts
 * the chart, and a sim held in component state reseeded a fresh market —
 * and dropped your buy/sell lines — on every toggle. Module state persists,
 * costs a fixed few hundred numbers, and recycles itself wholesale every
 * three minutes: lines cleared, a new market rolled, nothing accumulating.
 */
type HeroSim = {
  candles: HeroCandle[];
  regime: { mode: string; left: number; vol: number; anchor: number };
  lines: { buy?: number; sell?: number };
  ticksLeft: number;
  bornAt: number;
};

/** One market PER VENUE: flip to pump.fun and it is pump.fun's chart, with
 *  pump.fun's buy line still on it — the point being that the app is the
 *  same app on every venue, and each one has its own tape. Bounded by the
 *  venue list; each sim is 34 candles and two numbers. */
const heroSims = new Map<number, HeroSim>();

function heroSimFor(venue: number): HeroSim {
  let s = heroSims.get(venue);
  // Recycled wholesale every three minutes — fresh walk, lines cleared —
  // so nothing accumulates however long the tab stays open.
  if (!s || Date.now() - s.bornAt > 180_000) {
    const candles = seedHeroCandles();
    s = {
      candles,
      regime: { mode: "grind", left: 5, vol: 1, anchor: candles[candles.length - 1]!.c },
      lines: {},
      ticksLeft: 4,
      bornAt: Date.now(),
    };
    heroSims.set(venue, s);
  }
  return s;
}

/** The b/s keycaps pin their lines here — callable from the dashboard, which
 *  is always mounted, so a press lands even while the tape is showing. */
function heroSimMark(venue: number, buy: boolean) {
  const s = heroSimFor(venue);
  const c = s.candles[s.candles.length - 1]!.c;
  if (buy) s.lines.buy = c;
  else s.lines.sell = c;
}

/** A realized P&L, popping off the chart and dissolving. */
type ChaPop = { id: number; x: number; y: number; text: string; kind: "profit" | "loss" };
let chaId = 0;

/** The sim's trader, one per venue, module-level like the sim itself: a
 *  position taken at a real price, held through real ticks, closed by real
 *  rules — take profit at +8%, stop at −5%, give up after twelve ticks. */
const heroTraders = new Map<number, { pos: { p: number; ticks: number } | null; cool: number }>();

function HeroChart({ venue }: { venue: number }) {
  const sim = heroSimFor(venue);
  const [, redraw] = useState(0);
  const [pops, setPops] = useState<ChaPop[]>([]);

  // The forming candle ticks several times a second and SEALS roughly every
  // five — highs and lows carve themselves onto the live candle before the
  // window shifts, instead of a finished candle appearing out of nowhere.
  useEffect(() => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    // Each visit to a venue is one act: the trader arrives flat, buys within
    // a second or so, and after the payoff the stage hands itself to the
    // next protocol — so the state resets on mount, one trade per showing.
    heroTraders.set(venue, { pos: null, cool: 3 + Math.floor(Math.random() * 3) });
    const t = setInterval(() => {
      const s = heroSimFor(venue);
      const last = s.candles[s.candles.length - 1]!;
      if (s.ticksLeft <= 0) {
        s.ticksLeft = 3 + Math.floor(Math.random() * 2);
        s.regime = stepRegime(s.regime);
        s.candles = [...s.candles.slice(1), { o: last.c, h: last.c, l: last.c, c: last.c }];
      } else {
        s.ticksLeft -= 1;
        s.candles = [...s.candles.slice(0, -1), tickHeroCandle(last, s.regime)];
      }

      // The trade is part of the tick: the buy line goes down at a real
      // price, the position rides real candles, and the number that pops
      // is exactly the distance between the two lines on a $2,000 stake.
      const tr = heroTraders.get(venue) ?? { pos: null, cool: 6 };
      heroTraders.set(venue, tr);
      const price = s.candles[s.candles.length - 1]!.c;
      if (tr.pos) {
        tr.pos.ticks += 1;
        const pnl = (price - tr.pos.p) / tr.pos.p;
        if (pnl >= 0.08 || pnl <= -0.05 || tr.pos.ticks >= 12) {
          s.lines.sell = price;
          const dollars = Math.round(2000 * pnl);
          const h2 = Math.max(...s.candles.map((c) => c.h));
          const l2 = Math.min(...s.candles.map((c) => c.l));
          // Destination: just under the [t] TRADES [c] CHART bar — the coin
          // flies up from the market and lands there, every time.
          void h2;
          void l2;
          const yy = 13;
          const pop: ChaPop = {
            id: chaId++,
            // One spot, every time: under the [t]/[c] tabs. A position that
            // wandered between pops read as shifty — committed beats varied.
            x: 22,
            y: yy,
            text: `${dollars >= 0 ? "+" : "−"}$${Math.abs(dollars).toLocaleString()}`,
            kind: dollars >= 0 ? "profit" : "loss",
          };
          setPops((ps) => [...ps.slice(-2), pop]);
          setTimeout(() => setPops((ps) => ps.filter((q) => q.id !== pop.id)), 2100);
          tr.pos = null;
          tr.cool = 999; // one act per showing — the flip brings the next market
          setTimeout(() => window.dispatchEvent(new Event("trenches:cha-done")), 2300);
        }
      } else if (tr.cool-- <= 0 && Math.random() < 0.5) {
        s.lines.buy = price;
        s.lines.sell = undefined;
        tr.pos = { p: price, ticks: 0 };
      }

      redraw((n) => n + 1);
    }, 300);
    return () => clearInterval(t);
  }, [venue]);

  const shown = sim.candles;
  const hi = Math.max(...shown.map((c) => c.h));
  const lo = Math.min(...shown.map((c) => c.l));
  const y = (p: number) => ((hi - p) / Math.max(1e-9, hi - lo)) * 84 + 6;
  const last = shown[shown.length - 1]!;
  // Candles stop short of the right edge: the last ~6% is the label gutter,
  // so the newest candle forms BESIDE the price instead of over it.
  const w = 94 / HERO_CANDLES;
  return (
    <div className="relative h-full w-full">
      {pops.map((p) => (
        <span
          key={p.id}
          className="cha absolute"
          style={{ left: `${p.x}%`, top: `${p.y}%` }}
        >
          <span className={`cha-zoom cha-big ${p.kind === "profit" ? "text-good" : "text-bad"}`}>
            {p.text.split("").map((ch, i, arr) => (
              <span
                key={i}
                className="cha-ch"
                style={{ animationDelay: `${Math.abs(i - (arr.length - 1) / 2) * 55}ms` }}
              >
                {ch}
              </span>
            ))}
          </span>
        </span>
      ))}
      {/* The live price rule, dashes yielding to their label — drawn in
          AFTER the candle sweep lands, so the entrance has an order:
          the market first, then the readings on it. */}
      <div
        className="rule-in absolute right-0 left-0 flex items-center gap-2"
        style={{ top: `${y(last.c)}%`, transform: "translateY(-50%)", animationDelay: `${HERO_CANDLES * 22 + 250}ms` }}
      >
        <div className="flex-1 border-t border-dashed border-muted-foreground opacity-50" />
        <span className="pr-1 text-[10px] leading-none text-muted-foreground">{usd(1_070_000 * last.c)}</span>
      </div>
      {([["buy", sim.lines.buy], ["sell", sim.lines.sell]] as const).map(([side, p]) => {
        // A line whose price has left the visible range stays off rather
        // than pinning dishonestly to an edge — same rule as the app.
        if (!p || p <= lo || p >= hi) return null;
        const good = side === "buy";
        return (
          <div
            key={side}
            className="rule-in absolute right-0 left-0 flex items-center gap-2"
            style={{
              top: `${y(p)}%`,
              transform: "translateY(-50%)",
              animationDelay: Date.now() - sim.bornAt < 3500 ? `${HERO_CANDLES * 22 + 400}ms` : "0ms",
            }}
          >
            <div className={`flex-1 border-t border-dashed opacity-60 ${good ? "border-good" : "border-bad"}`} />
            <span className={`pr-1 text-[10px] leading-none ${good ? "text-good" : "text-bad"}`}>{side}</span>
          </div>
        );
      })}
      {shown.map((c, i) => {
        const up = c.c >= c.o;
        const top = y(Math.max(c.o, c.c));
        const bot = y(Math.min(c.o, c.c));
        const x = 94 - (shown.length - i) * w;
        const delay = { animationDelay: `${i * 22}ms` };
        return (
          <div key={i}>
            <div
              className={`candle-in absolute w-px ${up ? "bg-good" : "bg-bad"}`}
              style={{ left: `${x + 0.45 * w}%`, top: `${y(c.h)}%`, height: `${y(c.l) - y(c.h)}%`, ...delay }}
            />
            <div
              className={`candle-in absolute rounded-[1px] ${up ? "bg-good" : "bg-bad"}`}
              style={{
                left: `${x}%`,
                top: `${top}%`,
                width: `${w * 0.8}%`,
                height: `${Math.max(1.5, bot - top)}%`,
                ...delay,
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * The hero panel, running.
 *
 * A still of a trading dashboard says nothing about the thing that matters —
 * that it moves. So the tape fills, latency wobbles, the block advances and the
 * position follows the prints, each on its own clock the way it would on-chain.
 */
function LiveDashboard({ onView }: { onView?: (v: "tape" | "chart") => void }) {
  const [trades, setTrades] = useState<Trade[]>(() =>
    Array.from({ length: MAX_ROWS }, (_, i) => ({ ...newTrade(), at: Date.now() - i * 1500 })),
  );
  const [ms, setMs] = useState(42);
  const [block, setBlock] = useState(8_412_993);
  const [pooled, setPooled] = useState(42.85);
  // How many rows the box can actually show. Measured, not guessed: a fixed
  // count fills the frame at one window size and leaves a gap at every other.
  const box = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState(28);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const [balance, setBalance] = useState(0.482);
  const [realized, setRealized] = useState(0.0184);
  const [, tick] = useState(0);
  // Trades and Chart take turns in the same frame, the way the app's own
  // panels do — cramming both halved each until neither read as a screen.
  const [view, setView] = useState<"tape" | "chart">("chart");
  // Mirrors venueIdx (declared below) for handlers wired in a mount-once
  // effect — a captured index would pin every keycap fill to venue zero.
  const venueRef = useRef(0);
  useEffect(() => onView?.(view), [view, onView]);
  const pick = (v: "tape" | "chart") => setView(v);

  // No auto-flip: the panel is YOURS. t and c switch it, exactly the keys
  // the app itself uses — the same contract as the b/s keycaps above.
  useEffect(() => {
    const typing = () => {
      const el = document.activeElement;
      return el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement;
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat || typing()) return;
      if (e.key === "t") setView("tape");
      if (e.key === "c") setView("chart");
    };
    window.addEventListener("keydown", onDown);
    return () => window.removeEventListener("keydown", onDown);
  }, []);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout>;
    const measure = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        setRows((prev) => (prev === rowCap() ? prev : rowCap()));
      }, 120);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    // The cap depends on viewport width, which the box's own size may not
    // change — so watch the window too, or crossing xl leaves a stale cap.
    window.addEventListener("resize", measure);
    return () => {
      clearTimeout(timer);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Top the buffer up whenever the box grows, so the tape is full immediately
  // rather than filling in over the first half-minute.
  useEffect(() => {
    setTrades((prev) =>
      prev.length >= rows
        ? prev
        : [
            ...prev,
            ...Array.from({ length: Math.max(0, Math.min(rows, rowCap()) - prev.length) }, (_, i) => ({
              ...newTrade(),
              at: Date.now() - (prev.length + i) * 1500,
            })),
          ].slice(0, rowCap()),
    );
  }, [rows]);

  useEffect(() => {
    // Someone who asked for less motion should not get a dashboard that never
    // stops twitching.
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ages = setInterval(() => tick((n) => n + 1), 1000);
    const blocks = setInterval(
      () => setBlock((b) => b + 2 + Math.floor(Math.random() * 15)),
      2000,
    );
    const latency = setInterval(() => setMs(28 + Math.round(Math.random() * 64)), 1500);

    // A keycap press prints an order of your own, marked the way the app marks
    // yours, and moves the position with it.
    const onTrade = (e: Event) => {
      const side = (e as CustomEvent).detail as "buy" | "sell" | "dump";
      const buy = side === "buy";
      const t = { ...newTrade(buy, true), mine: true };
      heroSimMark(venueRef.current, buy);
      // A dump is the whole position, so it prints larger than a slice.
      if (side === "dump") t.eth = Math.max(t.eth, 1.4 + Math.random());
      setTrades((prev) => [t, ...prev].slice(0, rowCap()));
      setPooled((p) => Math.max(8, p + t.eth * (buy ? 0.9 : -0.4)));
      setBalance((b) => Math.min(9, b + t.eth * (buy ? 0.012 : 0.035)));
      setRealized((r) => r + t.eth * (buy ? 0.004 : 0.011));
    };
    window.addEventListener("trenches:trade", onTrade);

    const tape = setInterval(() => {
      // Trades arrive in bursts, not on a metronome: a block lands and several
      // swaps print at once. Mostly one or two, sometimes a flurry of a dozen.
      const r = Math.random();
      const burst = r < 0.5 ? 1 : r < 0.72 ? 2 : r < 0.86 ? 4 : r < 0.95 ? 8 : 12;

      // A real flurry is usually one-sided — everyone piling in, or everyone
      // getting out — so a burst leans hard to one side rather than alternating
      // green and red, which is what a random tape gives you and a real one
      // never does.
      const sweep = Math.random() > 0.5;
      const batch = Array.from({ length: burst }, () =>
        newTrade(burst > 2 ? (Math.random() > 0.2 ? sweep : !sweep) : undefined, true),
      );

      // Deep enough to fill the panel: the tape should look like it is running
      // out of room, not like eleven rows and a lot of empty frame.
      setTrades((prev) => [...batch, ...prev].slice(0, rowCap()));

      // Everything downstream follows the prints, so the numbers agree with
      // the tape instead of drifting independently of it.
      const eth = (f: (t: Trade) => number) => batch.reduce((sum, t) => sum + f(t), 0);
      setPooled((p) => Math.max(8, p + eth((t) => t.eth * (t.buy ? 0.9 : -0.4))));
      setBalance((b) => Math.min(9, b + eth((t) => t.eth * (t.buy ? 0.012 : 0.035))));
      setRealized((r) => r + eth((t) => t.eth * (t.buy ? 0.004 : 0.011)));
    }, 1200);

    return () => {
      clearInterval(ages);
      clearInterval(blocks);
      clearInterval(latency);
      clearInterval(tape);
      window.removeEventListener("trenches:trade", onTrade);
    };
  }, []);

  const age = (at: number) => {
    const s = Math.max(0, Math.round((Date.now() - at) / 1000));
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m`;
  };

  // Which venue the header is showing, so the balances can be quoted in its own
  // currency. It lives here rather than inside the header because two parts of
  // the panel have to agree about it.
  const [venueIdx, setVenueIdx] = useState(0);
  venueRef.current = venueIdx;
  const quote = VENUES[venueIdx]!.quote;

  return (
    <div className="flex h-full min-h-[380px] flex-col gap-3 p-4 text-[12px] leading-relaxed xl:min-h-[520px] xl:p-6 xl:text-[13px]">
      <div className="flex items-start justify-between gap-3">
        <VenueFlip i={venueIdx} setI={setVenueIdx} />
        {/* Latency over block height — the two numbers you actually watch. */}
        <span className="text-right leading-tight">
          <span className={`block ${ms < 200 ? "text-good" : "text-brand"}`}>{ms}ms</span>
          <span className="block text-muted-foreground">{block.toLocaleString("en-US")}</span>
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {[
          ["Balance", `${balance.toFixed(3)} ${quote}`],
          ["Realized", `${realized >= 0 ? "+" : ""}${realized.toFixed(4)} ${quote}`],
          ["Pooled", `${pooled.toFixed(2)} ${quote}`],
        ].map(([k, v]) => (
          <div key={k} className="rounded-sm border border-border px-3 py-2">
            <div className="mb-1 text-[10px] tracking-wider text-muted-foreground uppercase">{k}</div>
            <div key={v} className="stat-tick font-display text-[16px] text-foreground xl:text-[20px]">
              {v}
            </div>
          </div>
        ))}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-sm border border-border">
        <div className="flex gap-4 border-b border-border px-2.5 py-1.5 text-[10px] tracking-wider">
          {/* No uppercase transform: the key IS lowercase — [t], [c] — and a
              tab that shouts [T] teaches the wrong key. */}
          {(["tape", "chart"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => pick(v)}
              className={`cursor-pointer tracking-wider ${
                view === v ? "text-brand" : "text-muted-foreground"
              }`}
            >
              [{v === "tape" ? "t" : "c"}] {v === "tape" ? "TRADES" : "CHART"}
            </button>
          ))}
        </div>
        <div ref={box} className={`min-h-0 flex-1 overflow-hidden ${view === "chart" ? "p-3" : ""}`}>
          {view === "chart" ? (
            <HeroChart key={venueIdx} venue={venueIdx} />
          ) : (
          <table className="w-full table-fixed align-top">
            <tbody className="text-muted-foreground">
              {trades.slice(0, rows).map((t) => (
                <tr
                  key={t.id}
                  className={`border-b border-border/50 last:border-0 ${t.fresh ? "tape-row" : ""} ${
                    t.mine ? "bg-brand/10 text-foreground" : ""
                  }`}
                >
                  <td className="h-7 w-7 py-[7px] pl-2 text-center align-middle text-[14px] leading-none">
                    {t.mine ? "⭐" : ""}
                  </td>
                  <td className="px-2.5 py-[7px]">{age(t.at)}</td>
                  <td className={t.buy ? "text-good" : "text-bad"}>{t.buy ? "BUY" : "SELL"}</td>
                  <td className="text-right">{t.eth.toFixed(6)}</td>
                  <td className="px-2.5 text-right">{usd(t.cap)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="text-brand">[ ]</span> buy 0.05
        <span className="text-brand">{"{ }"}</span> slippage 5%
        <span className="text-brand">&lt; &gt;</span> priority auto
      </div>
    </div>
  );
}

function TerminalPreview({
  src,
  title = "trenches of robinhood",
  flush,
  fill,
  float,
  children,
}: {
  src?: string;
  /** Not rendered — kept as the accessible name for the screenshot. */
  title?: string;
  /** Sit in place instead of bleeding off the right edge (used by the tour). */
  flush?: boolean;
  /** Stretch to the height of the container (the full-screen tour). */
  fill?: boolean;
  /** Hero treatment: overhang the column and lift off the page. */
  float?: boolean;
  children?: React.ReactNode;
}) {
  // The hero's window wears the brand, not a screen name — it flips between
  // tape and chart, and renaming itself mid-flip read as indecision.
  const shown =
    src || children ? (
      title
    ) : (
      <>
        <span className="text-brand">▚</span> Trenches
      </>
    );
  return (
    <div
      className={`w-full ${fill ? "h-full" : ""} ${
        // Overhanging the column is what makes it read as floating ABOVE the
        // page rather than sitting in a slot on it. The section clips it.
        float ? "" : ""
      }`}
    >
      <div
        className={`isolate transform-gpu overflow-hidden rounded-md border border-border bg-card ${
          float
            ? "shadow-[0_40px_90px_-25px_rgba(0,0,0,0.65)] ring-1 ring-foreground/5"
            : "shadow-2xl"
        } ${fill ? "flex h-full flex-col" : ""}`}
      >
        {/* The window frame: traffic lights, and the screen's name in the
            middle — every feature shot says which screen it is. */}
        <div className="relative flex items-center gap-1.5 border-b border-border bg-foreground/[0.03] px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-[#ff5f57]" />
          <span className="h-3 w-3 rounded-full bg-[#febc2e]" />
          <span className="h-3 w-3 rounded-full bg-[#28c840]" />
          <span className="pointer-events-none absolute inset-x-0 text-center text-[11px] tracking-wide text-muted-foreground">
            {shown}
          </span>
        </div>

        {src ? (
          <img
            src={src}
            alt={`Trenches — ${title}`}
            className={fill ? "block h-full w-full object-cover object-top" : "block w-full"}
          />
        ) : children ? (
          <div
            className={
              fill
                ? "flex-1 overflow-hidden"
                : "no-scrollbar min-h-[300px] w-full min-w-0 overflow-x-auto overflow-y-hidden"
            }
          >
            {children}
          </div>
        ) : (
          <LiveDashboard onView={() => {}} />
        )}
      </div>
    </div>
  );
}

/**
 * The chain picker, as the app draws it.
 *
 * `select_table` in the TUI: a centred box, a plain list of networks on the
 * left, and the selected one's mark filling a panel on the right. No headers —
 * a single-column list does not need a column name, and the app skips the row
 * rather than leaving a gap where one would have been.
 *
 * The mark and the name, and nothing else. Which venues a chain carries is a
 * different question from which chain you are on, and answering it here turned
 * a picker into a features table.
 */
function MockChainSelect({
  groups,
}: {
  groups: { chain: string; src?: string; soon?: boolean }[];
}) {
  const [sel, setSel] = useState(0);
  const active = groups[sel]!;

  return (
    <div className="flex h-full min-h-0 items-center justify-center p-5">
      <div className="flex min-h-[240px] w-full max-w-[620px] flex-col overflow-hidden rounded-sm border border-border sm:flex-row">
        {/* The list. Fixed width, like the app's 26-column track — a list that
            resized with the longest name would jump as you moved through it. */}
        <div className="w-full shrink-0 border-b border-border sm:w-[210px] sm:border-r sm:border-b-0">
          <div className="border-b border-border px-4 py-2 text-[11px] tracking-wider text-brand uppercase">
            Select chain
          </div>
          {groups.map((g, i) => (
            <button
              key={g.chain}
              type="button"
              onMouseEnter={() => setSel(i)}
              onClick={() => setSel(i)}
              className={`flex h-8 w-full items-center gap-2 px-4 text-left text-[12px] transition-colors ${
                i === sel ? "bg-foreground/[0.08] text-foreground" : "text-muted-foreground"
              } ${g.soon ? "opacity-50" : ""}`}
            >
              <span className="w-2 shrink-0 text-brand">{i === sel ? "›" : ""}</span>
              <span className="truncate">{g.chain}</span>
              {g.soon && <span className="ml-auto shrink-0 text-[9px] tracking-wider uppercase">soon</span>}
            </button>
          ))}
        </div>

        {/* The selected chain's mark.
        
            Every box here is a FIXED size, and that is the whole point: this
            content changes on hover, and anything that sized itself to what it
            held would resize five times as you ran down the list. The slot is
            the same square whether it holds a logo or the placeholder, the name
            gets a fixed line that clips rather than wraps, and the panel keeps a
            minimum height so a shorter chain cannot pull it in. */}
        <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-4 px-5 py-8">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center">
            {active.src ? (
              <img src={active.src} alt="" className="max-h-full max-w-full object-contain" />
            ) : (
              // No mark yet for the chains that are not live. A blank square
              // beats borrowing another chain's logo — and it is the same
              // square, so selecting one does not move anything.
              <div className="flex h-full w-full items-center justify-center rounded-sm border border-dashed border-border text-[10px] text-muted-foreground">
                soon
              </div>
            )}
          </div>
          <div className="h-5 w-full truncate text-center text-[13px] leading-5 text-foreground">
            {active.chain}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * A binding as physical keys: `[ ]` is two caps, not one wide one, because they
 * are two keys. A separator like `/` stays plain text between them.
 */
function Keys({ combo }: { combo: string }) {
  return (
    // `inline-flex`, not `flex`: a flex container is block-level, so sitting one
    // inside a sentence broke the line before and after it — "Press" / [T] / "to
    // open the theme picker" on three rows. `align-middle` then sits the cap on
    // the text's centre rather than its baseline, where the bevel hangs low.
    <span className="inline-flex items-center gap-1 align-middle">
      {combo.split(/\s+/).map((tok, i) =>
        tok === "/" ? (
          <span key={i} className="text-muted-foreground">
            /
          </span>
        ) : (
          <kbd key={i} className="keycap text-[11px]">
            {tok}
          </kbd>
        ),
      )}
    </span>
  );
}

/**
 * The `?` overlay — the same thing the app shows, on the same key.
 *
 * Someone reading about a keyboard-driven tool will try the key. Having it do
 * nothing on the site would be the one wrong answer.
 */
/**
 * The theme picker, on `T` — the same key that opens it in the terminal.
 *
 * A dropdown of every palette the app ships, previewing as you move through it,
 * so the page you are reading IS the swatch. Arrow keys walk the list because
 * this is a keyboard product and a picker you have to reach for the mouse to
 * use would be arguing with itself.
 */
function ThemeMenu({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [sel, setSel] = useState(() =>
    Math.max(0, THEMES.findIndex((t) => t.id === (savedThemeId() ?? DEFAULT_THEME))),
  );
  const listRef = useRef<HTMLDivElement>(null);
  // What to fall back to if this closes without a choice.
  const before = useRef<string | null>(null);

  useEffect(() => {
    if (!open) return;
    before.current = savedThemeId();

    const revert = () => {
      const t = byId(before.current);
      if (t) applyTheme(t);
      else clearTheme();
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        revert();
        onClose();
        return;
      }
      // hjkl beside the arrows: this mirrors a list you drive from the home
      // row in the app, and 39 entries is far enough that h/l earn a bigger
      // stride rather than doing nothing in a single column.
      const step = { ArrowDown: 1, j: 1, ArrowUp: -1, k: -1, l: 10, h: -10 }[e.key];
      if (step !== undefined) {
        e.preventDefault();
        setSel((i) => {
          const n = THEMES.length;
          const next = (((i + step) % n) + n) % n;
          applyTheme(THEMES[next]!);
          return next;
        });
        return;
      }
      if (e.key === "Enter") {
        e.preventDefault();
        const t = THEMES[sel]!;
        applyTheme(t);
        saveTheme(t.id);
        onClose();
      }
    };

    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, sel]);

  // Keep the highlighted row in view while arrowing through 39 of them.
  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector<HTMLElement>('[data-sel="true"]')?.scrollIntoView({ block: "nearest" });
  }, [open, sel]);

  if (!open) return null;

  const commit = (i: number) => {
    const t = THEMES[i]!;
    applyTheme(t);
    saveTheme(t.id);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Themes"
      onClick={() => {
        const t = byId(before.current);
        if (t) applyTheme(t);
        else clearTheme();
        onClose();
      }}
      className="fixed inset-0 z-50 flex items-start justify-center bg-background/70 p-4 pt-[12vh] backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[70vh] w-full max-w-md flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-[11px] tracking-wider text-muted-foreground uppercase">
          <span>Theme</span>
          <span>
            <kbd className="keycap">↑↓</kbd> <kbd className="keycap">⏎</kbd>{" "}
            <kbd className="keycap">esc</kbd>
          </span>
        </div>
        <div ref={listRef} className="no-scrollbar min-h-0 flex-1 overflow-y-auto py-1">
          {THEMES.map((t, i) => (
            <button
              key={t.id}
              type="button"
              data-sel={i === sel}
              onMouseEnter={() => {
                setSel(i);
                applyTheme(t);
              }}
              onClick={() => commit(i)}
              className={`flex w-full items-center gap-3 px-4 py-1.5 text-left text-[12px] transition-colors ${
                i === sel ? "bg-foreground/[0.08] text-foreground" : "text-muted-foreground"
              }`}
            >
              <span className="flex shrink-0 gap-[3px]">
                {[t.bg, t.brand, t.accent, t.good, t.bad].map((c, j) => (
                  <span
                    key={j}
                    className="size-3 rounded-[2px] border border-border"
                    style={{ background: c }}
                  />
                ))}
              </span>
              <span className="truncate">{t.name}</span>
              <span className="ml-auto shrink-0 text-[10px] opacity-60">
                {t.light ? "light" : "dark"}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ShortcutsOverlay({ open, onClose }: { open: boolean; onClose: () => void }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    // The page behind should not scroll under the sheet.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Keyboard shortcuts"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[80vh] w-full max-w-3xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5 text-[11px] tracking-wider text-muted-foreground uppercase">
          <span>Shortcuts</span>
          <span>
            <kbd className="keycap">esc</kbd> to close
          </span>
        </div>
        <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto p-4">
          {KEYS.map(([k, d]) => (
            <div
              key={k}
              className="flex gap-4 border-b border-border/50 px-2 py-[5px] text-[12px] transition-colors last:border-0 hover:bg-foreground/[0.06]"
            >
              <span className="w-[92px] shrink-0">
              <Keys combo={k!} />
            </span>
              <span className="text-muted-foreground">{d}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Every binding the app has, from the app's own key file. Shared by the
 * Shortcuts panel and the `?` overlay, so the two can never disagree.
 *
 * `src/shortcuts.json` is a COPY of trenches-bot/shortcuts.json — refresh it
 * with `bun run sync-shortcuts`. The bot's test suite fails if this copy has drifted,
 * so the staleness is caught where the change is made rather than here.
 *
 * This page used to keep its own hand-written list, which is how it came to say
 * `< >` was the Solana priority fee when that is `{ }`. A fourth copy of a list
 * drifts exactly as fast as the first three did.
 *
 * The shaping here matches the terminal's help: a decrease/increase pair share
 * one row, and a key that exists on only one dashboard says which.
 */
const KEYS: string[][] = (() => {
  type Entry = { key: string; desc: string; chains: string[] };
  const src = (shortcutData as { shortcuts: Entry[] }).shortcuts;
  const where = (c: string[]) =>
    c.length > 1 ? "" : c[0] === "evm" ? "  ·  EVM" : "  ·  Solana";
  const out: string[][] = [];
  for (let i = 0; i < src.length; i++) {
    const k = src[i]!;
    const next = src[i + 1];
    const a = k.desc.startsWith("decrease ") ? k.desc.slice(9) : null;
    const b = next?.desc.startsWith("increase ") ? next.desc.slice(9) : null;
    // Same pairing rule as the app: adjacent, same thing, same dashboards.
    if (a && b && a === b && next && String(k.chains) === String(next.chains)) {
      out.push([`${k.key} ${next.key}`, `${a} − / +${where(k.chains)}`]);
      i++;
      continue;
    }
    out.push([k.key, `${k.desc}${where(k.chains)}`]);
  }
  return out;
})();

function MockKeys({ keys }: { keys: string[][] }) {
  return (
    <div className="flex h-full min-h-0 flex-col p-5">
      {/* One column, scrolled inside the frame: the full set is long, and
          splitting it into columns made it read as a table of contents. */}
      <div className="no-scrollbar min-h-0 flex-1 overflow-y-auto rounded-sm border border-border">
        {keys.map(([k, d]) => (
          <div
            key={k}
            className="flex gap-3 border-b border-border/50 px-4 py-[5px] text-[12px] transition-colors last:border-0 hover:bg-foreground/[0.06] hover:text-foreground"
          >
            <span className="w-[92px] shrink-0">
              <Keys combo={k!} />
            </span>
            <span className="text-muted-foreground">{d}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The PnL calendar, as the app draws it.
 *
 * The same shape as the TUI's `L` screen: totals across the top, a Monday-first
 * month grid with the day's dollars under its date, then the winners and losers
 * that made it up. A day with no trades shows a rule rather than "$0" — a flat
 * day and a day you sat out are different facts, and the grid is the one place
 * that distinction is visible at a glance.
 */
/** A placeholder bar. The PnL panel shows shape, not figures. */
function Skel({ w, h = 8 }: { w: number | string; h?: number }) {
  return (
    <span
      aria-hidden="true"
      className="inline-block rounded-[2px] bg-foreground/10"
      style={{ width: typeof w === "number" ? `${w}px` : w, height: h }}
    />
  );
}

/**
 * The PnL calendar, as the app draws it — as a skeleton.
 *
 * Deliberately no figures. Invented profit on a marketing page is a number
 * somebody will read as a claim, and the thing worth showing here is the shape:
 * a month as a grid, the days that traded against the days that did not, and
 * the winners and losers behind whichever one you are on. The keys are the only
 * literal content, because those ARE the feature.
 */
function MockPnl() {
  const DAYS = Array.from({ length: 31 });
  // Two days carry a figure. Thirty-one would be clutter, but one alone reads
  // as an outlier — a second says the cells hold amounts as a matter of course,
  // and the pair gives a sense of the range between an ordinary green day and a
  // standout one.
  //
  // One of them is red. A calendar where every marked day made money is a
  // brochure; the screen is for looking at how you actually traded, and a
  // losing day is half of what it is for.
  const MARKED: Record<number, { text: string; tone: "brand" | "good" | "bad" }> = {
    18: { text: "+$2.1K", tone: "brand" },
    6: { text: "+$340", tone: "good" },
    21: { text: "-$20", tone: "bad" },
  };
  // The 22nd — zero-indexed, and it lands mid-grid. `t` jumps here, so the
  // grid has to say where "here" is, and a marker near an edge reads as an
  // afterthought rather than as the day you are standing on.
  const TODAY = 21;
  const LEAD = 2; // the month does not open on a Monday

  // Which day is highlighted. Hover moves it and nothing else responds: in the
  // app the lists below reload for the day you pick, but reproducing that here
  // meant the boxes resized under the pointer as you swept the month.
  const [sel, setSel] = useState(9);

  const rows = (n: number) =>
    Array.from({ length: n }, (_, i) => (
      <div key={i} className="h-7 border-b border-border/40 last:border-0" />
    ));

  return (
    <div className="flex h-full min-h-0 items-center justify-center p-5">
      <div className="flex w-full flex-col gap-2 text-[12px]">
        {/* Totals. Labelled, so you can tell what the panel is showing; the
            figures themselves stay blank. */}
        <div className="flex items-center gap-3 rounded-sm border border-border px-3 py-2.5">
          <span className="text-[11px] tracking-wider text-brand uppercase">7D</span>
          <Skel w={54} h={11} />
          <span className="ml-auto text-[11px] tracking-wider text-brand uppercase">Month</span>
          <Skel w={54} h={11} />
        </div>

        {/* The month. Named, because which month you are looking at is the one
            piece of text on this panel that is navigation rather than a figure —
            the arrows move between them, and a grid with no month on it gives
            you nothing to move from. */}
        <div className="rounded-sm border border-border">
          <div className="border-b border-border px-3 py-1.5 text-[11px] text-foreground">
            July <span className="text-muted-foreground">2026</span>
          </div>
          <div className="grid grid-cols-7 gap-[2px] p-2">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} className="pb-1 text-center text-[10px] text-muted-foreground">
                {d}
              </div>
            ))}
            {Array.from({ length: LEAD }, (_, i) => (
              <div key={`lead-${i}`} />
            ))}
            {DAYS.map((_, i) => {
              const mark = MARKED[i];
              return (
                <button
                  key={i}
                  type="button"
                  onMouseEnter={() => setSel(i)}
                  onFocus={() => setSel(i)}
                  aria-label={
                    mark ? `day ${i + 1}, ${mark.text}` : i === TODAY ? "today" : `day ${i + 1}`
                  }
                  // Today gets a ring rather than a fill: `t` jumps back to it,
                  // and a key that returns you somewhere needs that somewhere to
                  // be visible. A fill would have competed with the best day.
                  className={`flex h-6 items-center justify-center rounded-[2px] text-[10px] font-semibold transition-colors ${
                    i === TODAY ? "ring-1 ring-brand/50 ring-inset " : ""
                  }${
                    mark?.tone === "brand"
                      ? "bg-brand/25 text-brand"
                      : mark?.tone === "good"
                        ? "bg-good/20 text-good"
                        : mark?.tone === "bad"
                          ? "bg-bad/20 text-bad"
                          : i === sel
                            ? "bg-foreground/[0.14]"
                            : "bg-foreground/[0.05] hover:bg-foreground/[0.1]"
                  }`}
                >
                  {mark?.text ?? ""}
                </button>
              );
            })}
          </div>
        </div>

        {/* Winners and losers for whichever day is selected. */}
        <div className="grid grid-cols-2 gap-2">
          {["Winners", "Losers"].map((title) => (
            <div key={title} className="rounded-sm border border-border">
              <div className="border-b border-border px-3 py-1 text-[10px] tracking-wider text-muted-foreground uppercase">
                {title}
              </div>
              {rows(12)}
            </div>
          ))}
        </div>

        {/* The only literal thing on the panel. */}
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground">
          <span>
            <span className="text-brand">h j k l</span> day
          </span>
          <span>
            <span className="text-brand">← →</span> month
          </span>
          <span>
            <span className="text-brand">1 2 3</span> 1D 7D 30D
          </span>
          <span>
            <span className="text-brand">t</span> today
          </span>
          <span>
            <span className="text-brand">esc</span> back
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The theme picker, as the app draws it — and a working one.
 *
 * A screenshot of a theme browser is the one screenshot that cannot do its job:
 * the whole point is what the colours do to a screen you are looking at. So
 * this list actually repaints the page, and the panel it sits in is the proof.
 *
 * Two modes, because the two questions are different. The list is the app's own
 * picker — names, scanned quickly, five swatches each. The grid answers "what
 * does it actually look like" without making you try all 39 one at a time.
 */
function ThemeSwatches({ t }: { t: (typeof THEMES)[number] }) {
  return (
    <span className="flex shrink-0 gap-[3px]">
      {[t.bg, t.brand, t.accent, t.good, t.bad].map((c, j) => (
        <span key={j} className="size-3 rounded-[2px] border border-border" style={{ background: c }} />
      ))}
    </span>
  );
}

/** A miniature of the app, drawn entirely from one palette's own colours. */
function ThemeCard({ t }: { t: (typeof THEMES)[number] }) {
  return (
    <div
      className="overflow-hidden rounded-[3px] border font-mono text-[8px] leading-[1.5]"
      style={{ background: t.bg, borderColor: t.border, color: t.dim }}
    >
      <div
        className="flex items-center gap-1.5 border-b px-1.5 py-[3px]"
        style={{ borderColor: t.border, background: t.card }}
      >
        <span style={{ color: t.brand }}>▚</span>
        <span style={{ color: t.fg }}>trenches</span>
        <span className="ml-auto" style={{ color: t.accent }}>
          21418045
        </span>
      </div>
      <div className="px-1.5 py-1">
        <div className="flex justify-between">
          <span>ETH</span>
          <span style={{ color: t.fg }}>1.284</span>
        </div>
        <div className="flex justify-between">
          <span>PnL</span>
          <span style={{ color: t.good }}>+0.44</span>
        </div>
        <div className="flex justify-between">
          <span>sell</span>
          <span style={{ color: t.bad }}>-0.08</span>
        </div>
      </div>
    </div>
  );
}

function MockThemes({ live }: { live: boolean }) {
  const [active, setActive] = useState(() => savedThemeId() ?? DEFAULT_THEME);
  const [mode, setMode] = useState<"list" | "grid">("list");
  const boxRef = useRef<HTMLDivElement>(null);

  const choose = (t: (typeof THEMES)[number]) => {
    applyTheme(t);
    saveTheme(t.id);
    setActive(t.id);
  };

  // hjkl, the way the app's own lists move. The grid's row stride is measured
  // off what was rendered rather than assumed — the column count is a
  // responsive breakpoint, and a hardcoded number jumps wrong on every screen
  // but one.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (!"hjkl".includes(e.key)) return;

      const cells = Array.from(
        boxRef.current?.querySelectorAll<HTMLButtonElement>("button[data-id]") ?? [],
      );
      if (!cells.length) return;

      const cols = new Set(cells.map((c) => c.offsetLeft)).size || 1;
      const at = Math.max(0, cells.findIndex((c) => c.dataset.id === active));
      const step = { l: 1, h: -1, j: cols, k: -cols }[e.key] ?? 0;
      if (!step) return;

      e.preventDefault();
      const next = Math.min(cells.length - 1, Math.max(0, at + step));
      const t = THEMES[next];
      if (!t) return;
      choose(t);
      cells[next]?.scrollIntoView({ block: "nearest" });
    };
    // Bound only while this is the step the reader is actually on.
    //
    // It used to check the panel was within the viewport instead, which was
    // right when one panel swapped its contents and wrong the moment they
    // became a stack: every panel is `sticky top-0 h-svh` now, so all of them
    // are on screen at all times and h/j/k/l changed the theme from anywhere on
    // the page.
    if (!live) return;
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, live]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-5">
      <div className="flex items-center justify-between gap-3 text-[12px]">
        <span className="whitespace-nowrap text-muted-foreground">
          <span className="text-brand">T</span> opens the theme picker
        </span>
        <span className="flex shrink-0 gap-1">
          {(["list", "grid"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              aria-pressed={mode === m}
              className={`rounded-[3px] border px-2 py-[2px] text-[11px] capitalize transition-colors ${
                mode === m
                  ? "border-brand text-brand"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {m}
            </button>
          ))}
        </span>
      </div>

      <div
        ref={boxRef}
        className={`no-scrollbar min-h-0 flex-1 overflow-y-auto rounded-sm border border-border ${
          mode === "grid" ? "grid grid-cols-2 content-start gap-2 p-2 sm:grid-cols-3 lg:grid-cols-4" : ""
        }`}
      >
        {THEMES.map((t) => {
          const on = t.id === active;
          return mode === "grid" ? (
            <button
              key={t.id}
              type="button"
              data-id={t.id}
              onClick={() => choose(t)}
              className={`flex flex-col gap-1 rounded-[3px] border p-1 text-left transition-colors ${
                on ? "border-brand" : "border-transparent hover:border-border"
              }`}
            >
              <ThemeCard t={t} />
              <span
                className={`truncate px-[2px] text-[10px] ${on ? "text-brand" : "text-muted-foreground"}`}
              >
                {t.name}
              </span>
            </button>
          ) : (
            <button
              key={t.id}
              type="button"
              data-id={t.id}
              onClick={() => choose(t)}
              className={`flex w-full items-center gap-3 border-b border-border/50 px-4 py-[5px] text-left text-[12px] transition-colors last:border-0 ${
                on
                  ? "bg-foreground/[0.08] text-foreground"
                  : "text-muted-foreground hover:bg-foreground/[0.06]"
              }`}
            >
              <span className="w-3 shrink-0 text-brand">{on ? "›" : ""}</span>
              <ThemeSwatches t={t} />
              <span className="truncate">{t.name}</span>
              <span className="ml-auto shrink-0 text-[10px] opacity-60">
                {t.light ? "light" : "dark"}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One command, in the middle of the frame.
 *
 * This is the first thing on the page now, so it has to answer "what do I type"
 * and nothing else. A clone, a toolchain and a build were three steps and a
 * prerequisite; an installer that fetches the right binary for the machine it
 * is run on is one line you can paste without reading.
 */
/** What a config actually looks like, next to the command that writes one. */
const EXAMPLE_CONFIG = `{
  "$schema": "https://trenches.sh/config.schema.json",
  "networks": [
    {
      "name": "robinhood-mainnet",
      "kind": "evm",
      "chain_id": 4663,
      "rpc": [
        "https://rpc.mainnet.chain.robinhood.com/rpc",
        "https://…alchemy.com/v2/YOUR_KEY"
      ]
    }
  ]
}`;

function MockInstall({ cmd, note, tbd }: { cmd: string; note: string; tbd?: boolean }) {
  return (
    <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4 p-5">
      <div className="w-full max-w-[460px]">
        <Cmd tbd={tbd}>{cmd}</Cmd>
      </div>
      <p className="m-0 max-w-[420px] text-center text-[12px] leading-relaxed text-muted-foreground">
        {note}
      </p>

      {/* The installer writes this for you and it works as-is. It is here
          because "what does it configure?" is the next question, and answering
          it with a shape is faster than answering it with prose. */}
      <div className="w-full max-w-[460px]">
        <p className="mb-2 text-center text-[11px] text-muted-foreground">
          Copy and edit <code className="text-foreground">~/.config/trenches/config.json</code>
        </p>
        <pre className="no-scrollbar m-0 overflow-x-auto rounded-sm border border-border bg-background/60 p-3 text-left text-[11px] leading-relaxed text-muted-foreground">
          <code>{EXAMPLE_CONFIG}</code>
        </pre>
        <p className="mt-2 text-center text-[11px] text-muted-foreground">
          <a href="/config" className="text-brand no-underline hover:underline">
            All the fields →
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * The scroll-driven feature tour.
 *
 * Each entry gets its own screenshot once they exist — drop the import into
 * `src` and the mock disappears for that step, no other change needed.
 */
const FEATURES: {
  name: string;
  /** Kept from the old standalone sections so the nav anchors still land. */
  id?: string;
  blurb: string;
  chrome: string;
  src?: string;
  /** A node, or "launches" for the interactive list that needs to know when it
   *  is the visible step. */
  mock: React.ReactNode | "launches" | "wallets" | "themes";
}[] = [
  {
    name: "Install",
    id: "install",
    blurb: "Install with curl, for configuration setting press D in the app for the full docs.",
    chrome: "Install",
    mock: (
      <MockInstall
        cmd="curl -fsSL https://trenches.sh/install | sh"
        note="macOS, Linux and Windows."
      />
    ),
  },
  {
    name: "Chains",
    id: "venues",
    blurb:
      "Press [C] to change chain — Robinhood Chain and Solana today, Base and BNB next, and one shortcut list covering them all.",
    chrome: "Venues",
    mock: (
      <MockChainSelect
        groups={[
          { chain: "Robinhood Chain", src: robinhood },
          { chain: "Solana", src: solana },
          { chain: "Base", soon: true },
          { chain: "BNB Chain", soon: true },
        ]}
      />
    ),
  },
  {
    name: "Accounts",
    id: "wallets",
    blurb:
      "Press [W] to switch accounts, with keys kept in a password-encrypted keystore on your own device.",
    chrome: "Accounts",
    mock: "wallets",
  },
  {
    name: "Pools",
    id: "pool",
    blurb:
      "Press [p] to switch pools or add one by CA, with price, liquidity and your position on one screen.",
    chrome: "Pool",
    mock: <PoolPanels />,
  },
  {
    name: "Trades",
    id: "tape",
    blurb:
      "Press [b] to buy or [x] to dump the lot, signed locally and out on the wire.",
    chrome: "Trades",
    mock: (
      <MockRows
        head={["age", "action", "amount SOL", "trader"]}
        rows={[
          ["2s", "BUY", "0.977778", "4YCD7moM…"],
          ["4s", "SELL", "0.655476", "2sK7gtAG…"],
          ["7s", "BUY", "0.244444", "GesfTA3X…"],
          ["9s", "SELL", "1.005363", "9M4giFFM…"],
          ["12s", "BUY", "0.488889", "5cjcW9wE…"],
        ]}
        sideCol={1}
      />
    ),
  },
  {
    name: "PnL",
    id: "pnl",
    blurb:
      "Press [L] for the calendar — every day you traded, what it made in dollars, and the trades behind it.",
    chrome: "PnL",
    mock: <MockPnl />,
  },
  {
    name: "Chart",
    id: "chart",
    blurb:
      "Press [c] for candlesticks — 1s to 1d intervals, a week of history per coin, and your own buys and sells ruled across at the price they filled.",
    chrome: "Candlestick Chart",
    mock: <MockCandles />,
  },
  {
    name: "Tokens",
    id: "trenches",
    blurb:
      "Press [f] to catch pools as they are deployed, straight off the chain rather than an aggregator.",
    chrome: "Live Launches",
    mock: "launches",
  },
  {
    name: "Themes",
    id: "themes",
    blurb:
      "Press [T] to change theme — 39 of them, and the one you pick here follows you around this site.",
    chrome: "Themes",
    mock: "themes",
  },
  {
    name: "Shortcuts",
    id: "keys",
    blurb: "Press [?] for help with keyboard shortcuts and settings.",
    chrome: "Shortcuts",
    mock: (
      <MockKeys keys={KEYS} />
    ),
  },
];

/**
 * The launches screen, as the app draws it.
 *
 * Same columns and the same title line as `trenches_view` in the TUI, and the
 * same interaction: j/k or the arrows move the cursor, and the selected row is
 * marked rather than merely tinted. Hovering picks a row too, because a mouse
 * is what a visitor has.
 */
function LaunchList({ live }: { live: boolean }) {
  const rows = [
    ["AORP", "3.41", "$41.2K", "12%", "4s", "ok"],
    ["GIGA", "18.02", "$218K", "63%", "19s", "ok"],
    ["MOTO", "27.88", "$337K", "91%", "47s", "ok"],
    ["PEPE2", "1.94", "$23.4K", "8%", "2m", "warn"],
    ["WIF3", "12.55", "$151K", "44%", "3m", "ok"],
    ["BONKR", "31.20", "$377K", "97%", "4m", "ok"],
    ["SNIPE", "0.82", "$9.9K", "3%", "5m", "warn"],
    ["TOAD", "9.14", "$110K", "31%", "6m", "ok"],
  ];
  const [sel, setSel] = useState(1);

  // Only while this step is the one on screen: a page-wide key handler that
  // steals j/k from every other section would be worse than no handler at all.
  useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      const down = e.key === "j" || e.key === "ArrowDown";
      const up = e.key === "k" || e.key === "ArrowUp";
      if (!down && !up) return;
      e.preventDefault();
      setSel((i) => Math.min(rows.length - 1, Math.max(0, i + (down ? 1 : -1))));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [live, rows.length]);

  const head = ["sym", "pooled SOL", "mkt cap", "bonded", "age", "risk"];

  return (
    <div className="flex h-full flex-col p-4 text-[12px] xl:p-5 xl:text-[13px]">
      {/* The panel title, worded as the app words it. */}
      <div className="mb-2 text-[11px] text-muted-foreground">
        Trenches — {rows.length} live coins <span className="text-brand">j/k</span> select ·{" "}
        <span className="text-brand">Enter</span> trade · <span className="text-brand">Esc</span> back
      </div>

      <div className="overflow-hidden rounded-sm border border-border">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border text-[11px] text-muted-foreground">
              <th className="w-6" />
              {head.map((h) => (
                <th key={h} className="px-2 py-1.5 text-left font-normal">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(([sym, pooled, cap, bonded, age, risk], i) => (
              <tr
                key={sym}
                onMouseEnter={() => setSel(i)}
                className={`border-b border-border/50 last:border-0 ${
                  i === sel ? "bg-foreground/[0.06] text-foreground" : "text-muted-foreground"
                }`}
              >
                {/* The cursor column, exactly as the table widget draws it. */}
                <td className="w-6 pl-2 text-brand">{i === sel ? "▸" : ""}</td>
                <td className="px-2 py-1">{sym}</td>
                <td className="px-2">{pooled}</td>
                <td className="px-2">{cap}</td>
                <td className={`px-2 ${Number.parseInt(bonded!) > 80 ? "text-good" : ""}`}>{bonded}</td>
                <td className="px-2">{age}</td>
                <td className={`px-2 ${risk === "warn" ? "text-bad" : "text-good"}`}>{risk}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * The wallet picker, as the app draws it: the actions first, then the keystores
 * on disk with the last-used one at the top. Same cursor and same keys as the
 * launches list.
 */
function WalletList({ live }: { live: boolean }) {
  const rows: { label: string; note?: string; action?: boolean }[] = [
    { label: "＋ Create new account", action: true },
    { label: "＋ Import private key", action: true },
    { label: "＋ Import seed phrase", action: true },
    { label: "~/.foundry/keystores/main", note: "last used" },
    { label: "~/.foundry/keystores/degen" },
    { label: "~/.foundry/keystores/cold" },
  ];
  const [sel, setSel] = useState(3);

  useEffect(() => {
    if (!live) return;
    const onKey = (e: KeyboardEvent) => {
      const down = e.key === "j" || e.key === "ArrowDown";
      const up = e.key === "k" || e.key === "ArrowUp";
      if (!down && !up) return;
      e.preventDefault();
      setSel((i) => Math.min(rows.length - 1, Math.max(0, i + (down ? 1 : -1))));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [live, rows.length]);

  return (
    // Centred in the frame, like the chain picker — both are the app's
    // `select_table`, and one floating in the middle while the other sat in the
    // top-left made them look like different screens.
    <div className="flex h-full min-h-0 items-center justify-center p-5">
      <div className="w-full max-w-[520px] text-[12px] xl:text-[13px]">
        <div className="mb-2 text-[11px] text-muted-foreground">
          Select an account or create one · <span className="text-brand">j/k</span> select ·{" "}
          <span className="text-brand">Enter</span> use · <span className="text-brand">Esc</span> back
        </div>

        <div className="overflow-hidden rounded-sm border border-border">
          {rows.map((r, i) => (
            <div
              key={r.label}
              onMouseEnter={() => setSel(i)}
              // Fixed row height, so moving down the list cannot resize it.
              className={`flex h-8 items-center gap-2 border-b border-border/50 px-2 last:border-0 ${
                i === sel ? "bg-foreground/[0.06] text-foreground" : "text-muted-foreground"
              }`}
            >
              <span className={`pl-1 ${r.action ? "text-foreground" : ""}`}>{r.label}</span>
              {r.note && <span className="text-brand">· {r.note}</span>}
            </div>
          ))}
        </div>

        {/* The app shows the derivation path for the highlighted choice. A
            fixed line: the two strings are different lengths, and letting the
            box size to them would shift the list above it on every hover. */}
        <div className="mt-2 h-4 truncate text-[11px] leading-4 text-muted-foreground">
          {rows[sel]?.action ? "m/44'/60'/0'/0/0 · EVM   m/44'/501'/0'/0' · Solana" : "password required to unlock"}
        </div>
      </div>
    </div>
  );
}

/**
 * The dashboard's two side-by-side panels, with the app's own titles, field
 * names and order: `Wallet [W]` on the left, `Pool [p]` on the right.
 */
function PoolPanels() {
  // Only the pool. The wallet readout that used to sit beside it is the
  // Accounts step's subject, and two panels of equal weight on a screen about
  // the pool left neither of them dominant.
  //
  // The fields and their order are the app's own, socials included: addresses
  // first and in full so they can be pasted into an explorer, then price and
  // depth, then who the token says it is. That last block is half the reason
  // the panel exists — a coin with nothing filled in is telling you something.
  const pool: [string, string, string?][] = [
    ["Protocol", "Uniswap V3 ETH/TRNCH 1%", "info"],
    ["Token", "0x7Ae21D4f9c05B3e6188A0Cd7412fB93a5E0d8C41"],
    ["Pool", "0x1F6bC29e5a0D7488bE3fA1c95d7E0242aB93F5D0"],
    ["Price", "105879485.0552 TRNCH/ETH"],
    ["Tick", "184787"],
    ["Mkt Cap", "~$0.02M"],
    ["Age", "2m (since graduation)"],
    ["Pooled", "3.578 ETH"],
    ["Pooled", "378861908 TRNCH"],
    ["Socials", "4/7 filled"],
    ["Web", "https://trenches.sh"],
    ["X", "https://x.com/msakiart"],
  ];

  const Panel = ({ title, rows }: { title: string; rows: [string, string, string?][] }) => (
    // `min-w-0` beside the basis: a flex item will not shrink below its content
    // by default, and a 42-character address would push this half wider than
    // its share instead of the value being clipped to fit.
    <div className="min-w-0 basis-1/2 rounded-sm border border-border">
      {/* The app puts the key that opens a panel in its title. */}
      <div className="border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">{title}</div>
      <div className="p-3">
        {rows.map(([k, v, tone], i) => (
          <div key={i} className="flex min-w-0 gap-3 py-[3px]">
            <span className="w-[86px] shrink-0 text-brand">{k}</span>
            <span
              className={`min-w-0 truncate ${
                tone === "good" ? "text-good" : tone === "info" ? "text-brand" : "text-foreground"
              }`}
            >
              {v}
            </span>
          </div>
        ))}
      </div>
    </div>
  );

  const knob = (key: string, label: string, value: string) => (
    <div key={label} className="flex gap-2 py-[3px]">
      <span className="w-10 shrink-0 text-brand">{key}</span>
      <span className="w-[68px] shrink-0 text-brand">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );

  return (
    // Side by side rather than stacked. The pool readout is tall and narrow and
    // the settings list is short and narrow; one above the other made a column
    // the height of the frame with empty space either side of it.
    //
    // Settings on the left, pool on the right: the knobs are the smaller, more
    // scannable block, and putting them first leaves the pool — the subject of
    // this screen — occupying the larger half.
    <div className="flex h-full min-h-0 items-center justify-center p-5">
      <div className="flex w-full flex-col items-stretch gap-3 text-[12px] sm:flex-row xl:text-[13px]">
        {/* Settings, as the app groups them: knobs, then modes, status last. */}
        <div className="flex min-w-0 basis-1/2 flex-col rounded-sm border border-border">
          <div className="border-b border-border px-3 py-1.5 text-[11px] text-muted-foreground">
            Settings
          </div>
          <div className="p-3">
            {knob("[ ]", "buy", "0.05 ETH")}
            {knob("( )", "sell", "100%")}
            {knob("{ }", "slippage", "5%")}
            {knob("< >", "impact", "3%")}
            {/* A rule between the sizes and the modes: they are set the same
                way but they are not the same kind of thing. */}
            <div className="my-2 border-t border-border/50" />
            {knob("[M]", "mode", "manual")}
            {knob("[g]", "guard", "OFF")}
          </div>
          {/* Pushed to the bottom of the column, so it lines up with the foot of
              the pool panel however tall that grows. */}
          <div className="mt-auto flex gap-3 border-t border-border px-3 py-1.5">
            <span className="w-[86px] shrink-0 text-brand">Status</span>
            <span className="text-muted-foreground">Watching TRNCH / ETH on Uniswap V3</span>
          </div>
        </div>

        <Panel title="Pool [p]" rows={pool} />
      </div>
    </div>
  );
}

/**
 * The candlestick chart, as the app draws it — green up, red down, your own
 * fills ruled across at the price they executed, and the live price dotted
 * over everything. Percent-positioned so it scales with its panel.
 */
function MockCandles() {
  // (left%, top%, height%, up, wickTop%, wickBottom%) — eyeballed from a real
  // session so the shape reads as a market, not as noise.
  const candles: [number, number, number, boolean, number, number][] = [
    [4, 70, 8, true, 66, 82],
    [10, 62, 10, true, 58, 74],
    [16, 64, 6, false, 60, 74],
    [22, 48, 18, true, 44, 68],
    [28, 44, 6, true, 40, 54],
    [34, 46, 10, false, 42, 60],
    [40, 54, 4, false, 50, 60],
    [46, 52, 4, true, 48, 58],
    [52, 40, 14, true, 36, 56],
    [58, 30, 12, true, 26, 44],
    [64, 32, 8, false, 28, 42],
    [70, 24, 10, true, 20, 36],
    [76, 22, 4, true, 18, 28],
    [82, 24, 6, false, 20, 32],
    [88, 16, 10, true, 12, 28],
  ];
  // The line hands off to its label instead of running underneath it — in
  // the TUI the label owns the axis gutter and the dashes stop at its edge.
  const line = (top: number, label: string, cls: string) => (
    <div
      className="absolute right-0 left-0 flex items-center gap-2"
      style={{ top: `${top}%`, transform: "translateY(-50%)" }}
    >
      <div className={`flex-1 border-t border-dashed ${cls} opacity-60`} />
      {/* The label owns its gutter: panel-colored backing and a layer above
          the candles, so a bar drawn at the right edge passes UNDER the
          reading instead of through it. */}
      <span className={`relative z-10 shrink-0 bg-card px-0.5 text-[10px] leading-none whitespace-nowrap ${cls}`}>
        {label}
      </span>
    </div>
  );
  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-2 flex justify-between text-[11px] text-muted-foreground">
        <span>
          ANSEM/SOL <span className="text-brand">1m candle</span> [,] [.]
        </span>
        <span>[t] <span className="text-brand">[c]</span> [o] [l]</span>
      </div>
      <div className="relative min-h-[220px] flex-1 overflow-hidden">
        {line(20, "sell", "border-bad text-bad")}
        {line(14, "0.002437", "border-muted-foreground text-muted-foreground")}
        {line(64, "buy", "border-good text-good")}
        {candles.map(([x, top, h, up, wt, wb], i) => (
          <div key={i}>
            <div
              className={`absolute w-px ${up ? "bg-good" : "bg-bad"}`}
              style={{ left: `${x + 2}%`, top: `${wt}%`, height: `${wb - wt}%` }}
            />
            <div
              className={`absolute rounded-[1px] ${up ? "bg-good" : "bg-bad"}`}
              style={{ left: `${x}%`, top: `${top}%`, width: "4%", height: `${h}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-muted-foreground">
        1s · 5s · 15s · 1m · 5m · 10m · 15m · 1h · 4h · 1d
      </div>
    </div>
  );
}

function MockRows({
  head,
  rows,
  sideCol,
  accent,
}: {
  head: string[];
  rows: string[][];
  sideCol?: number;
  accent?: number;
}) {
  return (
    <table className="w-full text-[13px]">
      <thead>
        <tr className="border-b border-border text-muted-foreground">
          {head.map((h) => (
            <th key={h} className="px-4 py-2.5 text-left font-normal">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="align-top text-muted-foreground">
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-border/50 last:border-0">
            {r.map((c, j) => (
              <td
                key={j}
                className={`px-4 py-2.5 ${
                  j === sideCol ? (c === "BUY" ? "text-good" : "text-bad") : ""
                } ${j === accent ? "text-brand" : ""}`}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MockStats({ stats, lines }: { stats: string[][]; lines: string[][] }) {
  return (
    <div className="flex h-full flex-col gap-4 p-5">
      {/* Three across only when there is room: at phone widths the figures
          collided with their own borders and clipped mid-number. */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {stats.map(([k, v]) => (
          <div key={k} className="rounded-sm border border-border p-4">
            <div className="mb-2 text-[11px] tracking-wider text-muted-foreground uppercase">{k}</div>
            <div className="font-display text-[20px] break-words text-foreground sm:text-[26px]">
              {v}
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-sm border border-border">
        {lines.map(([k, v]) => (
          <div
            key={k}
            className="flex flex-wrap justify-between gap-x-4 border-b border-border/50 px-4 py-2.5 text-[12px] last:border-0 sm:text-[13px]"
          >
            <span className="text-brand">{k}</span>
            <span className="text-muted-foreground">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MockList({ title, items, active }: { title: string; items: string[]; active: number }) {
  return (
    <div className="h-full p-5 text-[13px]">
      <div className="mb-3 text-[11px] tracking-wider text-muted-foreground uppercase">{title}</div>
      <div className="rounded-sm border border-border">
        {items.map((it, i) => (
          <div
            key={it}
            className={`border-b border-border/50 px-4 py-2.5 last:border-0 ${
              i === active ? "bg-foreground/5 text-brand" : "text-muted-foreground"
            }`}
          >
            {it}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Sticky preview, scrolling index. The screenshot swaps as each step passes
 * the middle of the viewport; the nav is also clickable, because a reader who
 * wants step four should not have to scroll for it.
 */
/**
 * The tour's state. It lives here rather than inside the section because the
 * INDEX now sits in the sidebar and the panel sits in the content column — two
 * components that have to agree on which step is showing.
 */
function useTour() {
  // -1 is the hero. The rail should say Welcome while you are on it, rather
  // than highlighting a feature you have not scrolled to yet.
  const [active, setActive] = useState(-1);
  const steps = useRef<(HTMLDivElement | null)[]>([]);
  const hero = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // Fire when a step crosses the middle band of the viewport, so the change
    // lands as the step reads rather than when it first peeks in at the edge.
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting) continue;
          if (e.target === hero.current) {
            setActive(-1);
            continue;
          }
          const i = steps.current.indexOf(e.target as HTMLDivElement);
          if (i >= 0) setActive(i);
        }
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: 0 },
    );
    if (hero.current) io.observe(hero.current);
    for (const el of steps.current) if (el) io.observe(el);
    return () => io.disconnect();
  }, []);

  const goTo = (i: number) =>
    i < 0
      ? scrollTo({ top: 0, behavior: "smooth" })
      : steps.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" });

  return { active, steps, hero, goTo };
}

type Tour = ReturnType<typeof useTour>;

/** The scrolling steps and the sticky panel. The index is in the sidebar. */
function FeatureTour({ tour }: { tour: Tour }) {
  // Below zero means the hero is on screen. Only the panel the reader is
  // actually on runs its live tickers — the stack renders all of them, and
  // eight simulated tapes would be eight timers for seven screens nobody is
  // looking at.
  const { active, steps } = tour;
  const panels = useRef<(HTMLDivElement | null)[]>([]);

  // Depth, not a pile.
  //
  // Every layer painted its own shadow into the same 32px strip above the
  // frame, so N layers meant N shadows in one place and the stack read as one
  // very dark band. Two fixes, both driven off how far the NEXT panel has risen
  // over this one:
  //
  //   - the shadow fades as a panel is covered, so only the arriving layer
  //     contributes one;
  //   - the covered panel scales down, drifts back and dims, so it reads as
  //     receding rather than being buried.
  //
  // Written on a rAF-throttled scroll listener rather than a scroll-driven CSS
  // animation: `animation-timeline: view()` is still uneven across browsers, and
  // this has to be right on the first paint after a resize too.
  useEffect(() => {
    let raf = 0;
    // Desktop only.
    //
    // The effect measures against `innerHeight`, and on a phone that is not a
    // constant: the browser's own chrome collapses and expands as you scroll,
    // which fires `resize` and re-derives every panel's transform mid-gesture.
    // The page appears to shift under your thumb. The stacking reads as decor
    // on a small screen anyway, where panels are nowhere near a viewport tall,
    // so it is not worth paying for.
    const desktop = matchMedia("(min-width: 1024px)");

    const clear = () => {
      for (const el of panels.current) {
        const inner = el?.firstElementChild as HTMLElement | undefined;
        if (!inner) continue;
        inner.style.transform = "";
        inner.style.filter = "";
        inner.style.removeProperty("--layer-shadow");
      }
    };

    const update = () => {
      raf = 0;
      const vh = innerHeight || 1;
      const els = panels.current;
      for (let i = 0; i < els.length; i++) {
        const el = els[i];
        const inner = el?.firstElementChild as HTMLElement | undefined;
        if (!el || !inner) continue;
        const next = els[i + 1];
        // 0 = nothing over this panel, 1 = fully covered by the next one.
        const p = next ? Math.min(1, Math.max(0, 1 - next.getBoundingClientRect().top / vh)) : 0;
        // Eased, so the recede happens mostly as the cover begins and then
        // settles — a linear scale reads as a mechanical shrink.
        const e = p * p * (3 - 2 * p);
        inner.style.transform = `scale(${1 - 0.08 * e}) translateY(${-26 * e}px)`;
        inner.style.filter = `brightness(${1 - 0.45 * e})`;
        inner.style.setProperty("--layer-shadow", String(1 - e));
      }
    };

    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };

    // Attached and detached with the breakpoint, so rotating a phone or
    // dragging a window across it settles into the right behaviour either way.
    const apply = () => {
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      if (!desktop.matches) {
        clear();
        return;
      }
      update();
      addEventListener("scroll", onScroll, { passive: true });
      addEventListener("resize", onScroll);
    };

    apply();
    desktop.addEventListener("change", apply);
    return () => {
      desktop.removeEventListener("change", apply);
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section id="features">
      {/* Panel and scroll steps share one grid cell: the steps give the column
          its height, the panel pins inside it. */}
      <div className="grid grid-cols-[minmax(0,1fr)] px-6 py-10 sm:px-8 lg:py-0">
        <div className="col-start-1 row-start-1 min-w-0">
          {FEATURES.map((f, i) => (
            <div
              key={f.name}
              id={f.id}
              ref={(el) => {
                steps.current[i] = el;
              }}
              className="snap-section flex min-h-[80vh] flex-col justify-center lg:min-h-[calc(100vh-3.5rem)]"
            >
              {/* On small screens the sticky panel is off, so each step carries
                  its own copy and image inline. */}
              {/* Screen first, then the words.
              
                  Desktop has the panel beside the copy, so both arrive at once.
                  Stacked on a phone, copy-first means scrolling past a paragraph
                  to reach the thing it describes — and the product is what
                  someone came to see. */}
              <Reveal className="min-w-0 lg:hidden">
                <div className="mb-2 text-[11px] tracking-[0.5px] text-brand uppercase">
                  {f.name}
                </div>
                <TerminalPreview title={f.chrome} src={f.src} flush>
                  {f.mock === "launches" ? (
                    <LaunchList live={false} />
                  ) : f.mock === "wallets" ? (
                    <WalletList live={false} />
                  ) : f.mock === "themes" ? (
                    <MockThemes live={false} />
                  ) : (
                    f.mock
                  )}
                </TerminalPreview>
                <p className="mt-5 text-[15px] text-muted-foreground">{f.blurb}</p>
              </Reveal>
            </div>
          ))}
        </div>

        {/* One sticky panel PER feature, stacked.
        
            Every panel pins to the top of the viewport and the next one rides up
            over it, so the screens layer instead of one panel swapping its
            insides. The swap was a cut between two frames; this is continuous —
            you can see the next screen arriving, and the one you just read stays
            underneath rather than vanishing.
        
            They share this grid cell with the scroll steps opposite, which is
            what gives the column its height and what the sidebar's index
            observes. */}
        <div className="col-start-1 row-start-1 hidden lg:block">
          {FEATURES.map((f, i) => (
            <div
              key={f.name}
              ref={(el) => {
                panels.current[i] = el;
              }}
              // Later panels must paint over earlier ones — without the rising
              // z-index the first one drawn wins and nothing appears to move.
              style={{ zIndex: i + 1 }}
              className="sticky top-0 flex h-svh flex-col justify-center py-8"
            >
              {/* Opaque, and the reason the layering reads at all: a
                  transparent panel would show the stack piling up behind it. */}
              <div className="layer h-full bg-background">
                <TerminalPreview title={f.chrome} src={f.src} flush fill>
                  {f.mock === "launches" ? (
                    <LaunchList live={i === active} />
                  ) : f.mock === "wallets" ? (
                    <WalletList live={i === active} />
                  ) : f.mock === "themes" ? (
                    <MockThemes live={i === active} />
                  ) : (
                    f.mock
                  )}
                </TerminalPreview>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * The board, between the tour and the footer.
 *
 * A trading floor's ticker, which is the one place a dot-matrix display is not
 * decoration — it is what this product is a terminal for. Sits where the tour
 * ends so the last thing before the footer is the pitch, moving.
 */
function LedBand({
  items,
  label,
  height = 64,
  dotSize = 5,
  dotQuantity = 9,
  className = "border-y border-border bg-card/40 py-6",
}: {
  items: string[];
  /** The same message as words, for anything that reads rather than looks. */
  label: string;
  /** Glyph height in CSS pixels — the board is this tall. */
  height?: number;
  dotSize?: number;
  dotQuantity?: number;
  className?: string;
}) {
  return (
    <div className={className}>
      <div style={{ height }} className="w-full">
        <LedTicker
          items={items}
          separator="★"
          speed={26}
          textSize={height}
          dotSize={dotSize}
          dotQuantity={dotQuantity}
          spread={1}
        />
      </div>
      {/* The canvas is `aria-hidden` — it is pixels, not text. */}
      <p className="sr-only">{label}</p>
    </div>
  );
}

/** Full-bleed band: a fixed label, then the protocols scrolling past it. */
function ProtocolMarquee() {
  return (
    <div className="shell flex items-stretch border-y border-border">
      <span className="z-[1] flex shrink-0 items-center whitespace-nowrap border-r border-border bg-background py-2 pr-[22px] pl-5 text-[11px] sm:pl-6 lg:pl-8 tracking-[0.1em] text-muted-foreground uppercase">
        Supported protocols
      </span>
      <div className="marquee flex-1">
        <div className="marquee-track" aria-label="Protocols you can trade on Trenches">
          {PROTOCOLS.map((p) => (
            <MarqueeItem key={p.name} {...p} />
          ))}
          {PROTOCOLS.map((p) => (
            <MarqueeItem key={`${p.name}-clone`} {...p} clone />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * The two vertical rules marking where content starts and stops.
 *
 * Fixed rather than per-section, so they run unbroken down the page — a rule
 * that restarts at every section reads as a border, not a measure. Sits above
 * section backgrounds (which are opaque) but below the sticky header, and never
 * takes a click.
 */
const REPO = "asyncswap/trenches";

/** GitHub's mark, inline so the page makes no third-party requests for it. */
function GitHubMark({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="currentColor" className={className}>
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27s1.36.09 2 .27c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

/**
 * Stars, read once from the public API.
 *
 * Renders the mark alone until a count arrives, if the request fails, and if
 * the answer is zero. A link that works is the point; a dash where a number
 * should be reports our own outage to a visitor who did not ask, and a proud
 * "0" is worse than saying nothing.
 */
function GitHubLink({ className = "" }: { className?: string }) {
  const [stars, setStars] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`https://api.github.com/repos/${REPO}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (live && typeof d?.stargazers_count === "number") setStars(d.stargazers_count);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <a
      href={`https://github.com/${REPO}`}
      target="_blank"
      rel="noreferrer"
      aria-label={`Trenches on GitHub${stars ? ` — ${stars} stars` : ""}`}
      // Sized and bordered like the buttons it sits beside — same 36px box, same
      // radius — so a rail holding Install and this one reads as a row of
      // controls rather than a button with a link stuck to it. Secondary
      // styling on purpose: Install is the ask, this is the aside.
      className={`inline-flex h-9 items-center gap-2 rounded-sm border border-border px-3 text-muted-foreground no-underline transition-colors hover:border-foreground/30 hover:bg-foreground/5 hover:text-foreground ${className}`}
    >
      <GitHubMark />
      {/* Only once there are some. A count of nothing is worse than no count:
          it draws the eye to the number and then reports zero. Just the digits
          — the mark beside them already says what is being counted. */}
      {stars !== null && stars > 0 && (
        <span className="text-[13px] leading-none tabular-nums">
          {stars >= 1000 ? `${(stars / 1000).toFixed(1)}k` : stars}
        </span>
      )}
    </a>
  );
}

/**
 * The latest published version, from the releases API.
 *
 * The list endpoint, not `/latest` — while this is a beta the releases are
 * marked pre-release, and `/latest` skips those. Same reason the installer
 * reads the list, and the same trap: the endpoint that sounds right reports
 * nothing for a release that exists.
 *
 * Renders nothing until it has an answer, and nothing if it never gets one.
 */
function ReleaseTag() {
  const [tag, setTag] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(`https://api.github.com/repos/${REPO}/releases?per_page=1`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const t = Array.isArray(d) ? d[0]?.tag_name : null;
        if (live && typeof t === "string") setTag(t);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  if (!tag) return null;
  return (
    <a
      // The tag itself, not /releases/latest. That path excludes pre-releases,
      // and while every release here is one it redirects to the release index —
      // so the version you are looking at linked to a list rather than to
      // itself. Same trap the installer and the update check both hit.
      href={`https://github.com/${REPO}/releases/tag/${tag}`}
      target="_blank"
      rel="noreferrer"
      // The display face, at a size that reads as a label rather than as fine
      // print. It is the one piece of version information on the page, and the
      // pixel type is what the rest of the site sounds like.
      className="pointer-events-auto font-display text-[17px] font-semibold text-muted-foreground no-underline transition-colors hover:text-foreground"
    >
      {tag}
    </a>
  );
}

/** Every venue the bot trades, in the order it learned them. */
const VENUES: { name: string; src: string; quote: string }[] = [
  { name: "ROBINHOOD", src: robinhood, quote: "ETH" },
  { name: "PONS.FAMILY", src: pons, quote: "ETH" },
  // The two Uniswaps are separate venues with separate plumbing — different
  // routers, different pool identity — so they are listed apart even though
  // they wear the same mark.
  { name: "UNISWAP V3", src: uniswap, quote: "ETH" },
  { name: "UNISWAP V4", src: uniswap, quote: "ETH" },
  // Flaunch coins pair against flETH, which redeems 1:1 with ETH — so the
  // panel says ETH, which is what you actually spend and get back.
  { name: "FLAUNCH", src: flaunch, quote: "ETH" },
  // Uniswap's own launchpad: instant launches are plain v4 pools from birth,
  // crowd launches auction first and graduate into one.
  { name: "POOLS.TRADE", src: poolsTrade, quote: "ETH" },
  // pools.fun launches straight into a SushiSwap V3 pool. A v3 router is bound
  // to one factory, so these are not reachable through Uniswap's — they route
  // through Sushi's own path. Most pair WETH; a minority pair USDG.
  { name: "POOLS.FUN", src: poolsFun, quote: "ETH" },
  // The Solana venues are quoted in SOL, and the panel says so — a balance
  // labelled ETH under a pump.fun header is the kind of small lie that makes
  // someone doubt the numbers that are real.
  { name: "SOLANA", src: solana, quote: "SOL" },
  { name: "PUMP.FUN", src: pump, quote: "SOL" },
];

/**
 * The venue name, revealed left to right.
 *
 * Each character arrives from slightly left of where it belongs, out of focus,
 * on a stagger — so the name reads as being wiped in rather than assembled from
 * moving parts. The old one leaves the way it came, and the swap happens while
 * nothing is on screen, so no character is caught being substituted and nothing
 * needs measuring or padding for names of different lengths.
 *
 * Written in CSS. The reference implementation wanted framer-motion, GSAP and
 * SplitText — a paid plugin — to express an opacity, a translate and a blur on
 * a delay.
 */
function VenueFlip({ i, setI }: { i: number; setI: React.Dispatch<React.SetStateAction<number>> }) {
  const [out, setOut] = useState(false);
  const venue = VENUES[i]!;

  // The venue holds the stage for one act of the chart's trade — buy, ride,
  // sell, the number — and flips when the payoff has had its moment (the
  // chart dispatches trenches:cha-done). The long timer is only a fallback
  // for an act that never gets going.
  const HOLD = 14000;
  const FLIP = 300;
  const STAGGER = 26;

  useEffect(() => {
    // Motion here is decoration. Anyone who has asked for less gets the names
    // without the spinning.
    const still = matchMedia("(prefers-reduced-motion: reduce)");
    if (still.matches) {
      const t = setInterval(() => setI((n: number) => (n + 1) % VENUES.length), 8000);
      return () => clearInterval(t);
    }

    let swap: ReturnType<typeof setTimeout> | undefined;
    let flipping = false;
    const doFlip = () => {
      if (flipping) return;
      flipping = true;
      setOut(true);
      // Swap at the far end of the stagger, when the last character has turned.
      swap = setTimeout(() => {
        setI((n: number) => (n + 1) % VENUES.length);
        setOut(false);
      }, FLIP + venue.name.length * STAGGER);
    };
    const hold = setTimeout(doFlip, HOLD);
    window.addEventListener("trenches:cha-done", doFlip);
    return () => {
      window.removeEventListener("trenches:cha-done", doFlip);
      clearTimeout(hold);
      clearTimeout(swap);
    };
  }, [i, venue.name.length, setI]);

  return (
    <span className="flex items-center gap-2.5">
      <img
        src={venue.src}
        alt=""
        className="h-[18px] w-[18px] shrink-0 object-contain transition-[transform,opacity] duration-300 xl:h-6 xl:w-6"
        style={{ transform: out ? "translateX(-10px)" : "none", opacity: out ? 0 : 1 }}
      />
      <span
        aria-label={venue.name}
        className="font-display text-[22px] whitespace-pre text-foreground xl:text-[30px]"
      >
        {venue.name.split("").map((ch, n) => (
          <span
            key={`${i}-${n}`}
            aria-hidden="true"
            className="inline-block"
            style={{
              transition: `transform ${FLIP}ms ease-out, opacity ${FLIP}ms ease-out, filter ${FLIP}ms ease-out`,
              transitionDelay: `${n * STAGGER}ms`,
              transform: out ? "translateX(-10px)" : "translateX(0)",
              opacity: out ? 0 : 1,
              filter: out ? "blur(6px)" : "blur(0)",
            }}
          >
            {ch}
          </span>
        ))}
      </span>
    </span>
  );
}

export function Gutters() {
  return (
    // Behind everything. The rules mark the measure; they are not meant to be
    // drawn across the content that sits on it, and the hero panel in
    // particular should cross the line rather than be scored by it.
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-[5] hidden lg:block">
      <div className="mx-auto flex h-full w-full max-w-[1388px]">
        <div className="w-[236px] shrink-0" />
        <div className="h-full flex-1 border-r border-border" />
      </div>
    </div>
  );
}

/**
 * Text with a sheen sweeping across it.
 *
 * A bright copy of the same string sits over the base, masked to a band that
 * travels left to right — so the label catches the light instead of blinking.
 * The second copy is `aria-hidden`, or a screen reader announces the word twice.
 */
function Shiny({ children, solid }: { children: string; solid?: boolean }) {
  return (
    <span className={`shiny${solid ? " shiny-solid" : ""}`}>
      <span>{children}</span>
      <span className="shiny-sheen" aria-hidden="true">
        {children}
      </span>
    </span>
  );
}

/** Desktop: a persistent rail. Everything global lives here, not in a bar. */
function SidebarNav({ tour }: { tour: Tour }) {
  const { active, goTo } = tour;
  // The highlight is one element that slides, rather than a border switching on
  // and off per item — that is what makes the movement continuous.
  const items = useRef<(HTMLButtonElement | null)[]>([]);
  const [marker, setMarker] = useState({ top: 0, height: 0 });

  useEffect(() => {
    // Welcome sits at index 0 of the list, so a feature at `active` is one row
    // further down.
    const el = items.current[active + 1];
    if (el) setMarker({ top: el.offsetTop, height: el.offsetHeight });
  }, [active]);

  return (
    <nav
      aria-label="Primary"
      className="sticky top-0 hidden h-svh w-[236px] shrink-0 flex-col justify-between self-start border-r border-border py-6 lg:flex"
    >
      <div className="flex min-h-0 flex-col gap-7">
        <a href="/" className="px-6 font-display text-[1.05rem] no-underline">
          <span className="text-brand">▚ </span>Trenches.sh
        </a>

        <div>
          <div className="relative">
            <span
              aria-hidden="true"
              className="absolute left-0 w-full border-l-2 border-brand bg-foreground/5 transition-[transform,height] duration-500 ease-out"
              style={{ height: marker.height, transform: `translateY(${marker.top}px)` }}
            />
            {[{ name: "Welcome" }, ...FEATURES].map((f, idx) => {
              const i = idx - 1;
              return (
              <button
                key={f.name}
                type="button"
                ref={(el) => {
                  items.current[idx] = el;
                }}
                onClick={() => goTo(i)}
                className={`relative flex w-full items-center border-l-2 border-transparent py-2 pr-6 pl-[22px] text-left text-sm transition-colors duration-300 ${
                  i === active ? "font-semibold text-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {f.name}
              </button>
              );
            })}
          </div>

          {/* Under the list, so the index reads as one block and the copy
              explains what you have landed on. */}
          <p className="mt-5 px-6 text-[13px] leading-relaxed text-muted-foreground">
            {active < 0
              ? "A terminal for trading memecoins on Robinhood Chain and Solana."
              : FEATURES[active]!.blurb}
          </p>
        </div>
      </div>

      {/* Primary: it is the one thing on this rail we are asking anyone to do,
          and an outlined button beside a list of outlined links is not asking. */}
      <div className="mx-6 flex items-center gap-4">
        <a
          href="#install"
          className="inline-flex h-9 items-center justify-center rounded-sm bg-foreground px-5 text-sm font-semibold text-background transition-colors hover:bg-brand"
        >
          <Shiny solid>Install</Shiny>
        </a>
        <GitHubLink />
      </div>
    </nav>
  );
}

/** Below lg the rail becomes a fixed bar with a sheet under it. */
function MobileHeader({
  menu,
  setMenu,
  goTo,
}: {
  menu: boolean;
  setMenu: (v: boolean) => void;
  goTo: (i: number) => void;
}) {
  return (
    <div className="lg:hidden">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
        <div className="flex h-14 items-center justify-between px-6">
          <a href="/" className="font-display text-[1.05rem] no-underline">
            <span className="text-brand">▚ </span>Trenches.sh
          </a>
          <button
            type="button"
            onClick={() => setMenu(!menu)}
            aria-expanded={menu}
            aria-controls="mobile-nav"
            aria-label={menu ? "Close menu" : "Open menu"}
            className="-mr-2 p-2"
          >
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {menu && (
          <nav id="mobile-nav" className="no-scrollbar max-h-[70svh] overflow-y-auto border-t border-border bg-background">
            {FEATURES.map((f, i) => (
              <button
                key={f.name}
                type="button"
                // Tapping closes the sheet — leaving it open over the section
                // you just jumped to would hide what you asked for.
                onClick={() => {
                  setMenu(false);
                  goTo(i);
                }}
                className="block w-full border-b border-border/50 px-6 py-3.5 text-left text-sm last:border-0 hover:text-brand"
              >
                {f.name}
              </button>
            ))}
          </nav>
        )}
      </header>
    </div>
  );
}

/** Three-way theme control: follow the OS, or override it either way. */
function ThemeToggle() {
  const [mode, setMode] = useState<"system" | "light" | "dark">("system");
  // A palette from /themes owns the page's colours AND its light/dark class, so
  // this switch stands down while one is active rather than half-overriding it.
  const [themed, setThemed] = useState(false);

  useEffect(() => {
    let saved: string | null = null;
    try {
      saved = localStorage.getItem("theme");
    } catch {}
    if (saved === "light" || saved === "dark") setMode(saved);
    setThemed(savedThemeId() !== null);
  }, []);

  useEffect(() => {
    if (themed) return;
    const dark =
      mode === "dark" ||
      (mode === "system" && matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", dark);
    try {
      if (mode === "system") localStorage.removeItem("theme");
      else localStorage.setItem("theme", mode);
    } catch {}
  }, [mode, themed]);

  // Picking here means picking the default look — so the palette goes.
  const pick = (key: "system" | "light" | "dark") => {
    if (themed) {
      clearTheme();
      setThemed(false);
    }
    setMode(key);
  };

  const opts = [
    { key: "system", icon: Monitor, label: "System theme" },
    { key: "light", icon: Sun, label: "Light theme" },
    { key: "dark", icon: Moon, label: "Dark theme" },
  ] as const;

  return (
    <fieldset className="flex items-center gap-1">
      <legend className="sr-only">Theme</legend>
      {opts.map(({ key, icon: Icon, label }) => (
        <button
          key={key}
          type="button"
          aria-label={label}
          aria-pressed={!themed && mode === key}
          onClick={() => pick(key)}
          className={`flex size-7 items-center justify-center rounded-sm transition-colors ${
            !themed && mode === key
              ? "bg-foreground/10 text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <Icon className="size-4" />
        </button>
      ))}
    </fieldset>
  );
}

const FOOTER_COLS: { title: string; links: { label: string; href: string; ext?: boolean }[] }[] = [
  // What the app does, and where to read about using it. The split is by
  // question rather than by destination: "what is this" on the left, "how do I
  // set it up" on the right.
  {
    title: "Trenches",
    links: [
      { label: "Accounts", href: "#wallets" },
      { label: "Chains", href: "#venues" },
      { label: "Pools", href: "#pool" },
      { label: "Trades", href: "#tape" },
    ],
  },
  {
    title: "Docs",
    links: [
      { label: "Install", href: "#install" },
      { label: "Configuration", href: "/config" },
      { label: "Support", href: "/support" },
      { label: "Shortcuts", href: "#keys" },
      // For anyone pointing an assistant at this.
      { label: "llms.txt", href: "/llms.txt", ext: true },
    ],
  },
  {
    title: "Socials",
    links: [
      { label: "GitHub", href: `https://github.com/${REPO}`, ext: true },
      { label: "X (@msakiart)", href: "https://x.com/msakiart", ext: true },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms", href: "/terms" },
      { label: "Privacy", href: "/privacy" },
      { label: "Licence", href: "/license" },
    ],
  },
];

export function SiteFooter() {
  return (
    <footer className="snap-section border-t border-border px-6 pt-16 pb-8 lg:px-10">
      <div className="flex flex-col gap-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:gap-8">
          <div className="flex w-full flex-col gap-4 lg:w-[180px] lg:shrink-0">
            <p className="m-0 text-[13px] text-muted-foreground">© 2026 Trenches.sh</p>
            <ThemeToggle />
          </div>

          <nav
            aria-label="Footer"
            className="grid w-full grid-cols-2 gap-x-8 gap-y-10 lg:flex-1 lg:grid-cols-3"
          >
            {FOOTER_COLS.map((col) => (
              <div key={col.title} className="flex flex-col gap-3">
                <h2 className="text-[13px] font-medium text-foreground">{col.title}</h2>
                <ul className="flex flex-col gap-1.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      <a
                        href={l.href}
                        {...(l.ext ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                        className="text-[13px] text-muted-foreground transition-colors hover:text-foreground"
                      >
                        {l.label}
                        {l.ext && <span className="sr-only"> (opens in a new tab)</span>}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>
        </div>

        {/* The wordmark as a full-width rule, the way the reference closes out. */}
        <div
          aria-hidden="true"
          className="font-display text-[clamp(48px,13vw,150px)] leading-[0.8] text-foreground/10 select-none"
          style={{ wordSpacing: "-0.22em" }}
        >
          trenches.sh
        </div>

        <p className="m-0 max-w-[70ch] text-[12px] leading-relaxed text-muted-foreground">
          Trenches is beta software for trading memecoins. Trading is risky and you
          can lose everything you put in. Nothing here is financial advice.
        </p>
      </div>
    </footer>
  );
}

export function App() {
  const [menu, setMenu] = useState(false);
  const [help, setHelp] = useState(false);
  const [themeMenu, setThemeMenu] = useState(false);
  const tour = useTour();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
      if (e.key === "?") {
        e.preventDefault();
        setHelp((v) => !v);
        return;
      }
      // `T` is the theme picker, the same key it is in the app.
      if (e.key === "T") {
        e.preventDefault();
        setThemeMenu((v) => !v);
        return;
      }
      // `x` dumps the position, the same key it is in the app on either
      // chain. `b` and `s` belong to their keycaps, which handle their own
      // press state.
      if (e.key === "x" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.repeat) {
        window.dispatchEvent(new CustomEvent("trenches:trade", { detail: "dump" }));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Snapping belongs to this page, not the document: the legal pages share the
  // footer, and a snap target down there hijacked their scroll.
  useEffect(() => {
    document.documentElement.classList.add("snap");
    return () => document.documentElement.classList.remove("snap");
  }, []);

  return (
    // `relative` so the corner below anchors to the top of the page rather
    // than to the viewport.
    <div className="relative mx-auto flex min-h-svh w-full max-w-[1388px] flex-col lg:flex-row">
      <Gutters />

      {/* Top right, on the same measure as everything else. Absolute rather
          than fixed: it belongs to the top of the page, not to the viewport, so
          it scrolls away instead of riding over the tour. Desktop only — below
          lg the header owns that corner and a second control would crowd the
          menu button. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 z-20 hidden lg:block">
        <div className="mx-auto flex w-full max-w-[1388px] items-center justify-end gap-3 px-10 pt-5">
          <ReleaseTag />
          <GitHubLink className="pointer-events-auto" />
        </div>
      </div>

      <ShortcutsOverlay open={help} onClose={() => setHelp(false)} />
      <ThemeMenu open={themeMenu} onClose={() => setThemeMenu(false)} />
      <MobileHeader menu={menu} setMenu={setMenu} goTo={tour.goTo} />
      <SidebarNav tour={tour} />

      <main className="relative flex min-w-0 flex-1 flex-col pt-14 lg:pt-0">
        <section
          ref={tour.hero}
          // `overflow-x-clip` at every size, and that is deliberate: the panel
          // is meant to run off the edge of the APP, not off the edge of the
          // browser. The section spans the 1388px canvas, so clipping here is
          // what makes the bleed stop at the product's own boundary instead of
          // spilling into the page margin beside it.
          className="scanlines snap-section relative flex h-[calc(100svh-3.5rem)] flex-col overflow-x-clip border-b border-border bg-background lg:h-auto lg:min-h-[calc(100vh-3.5rem)] lg:overflow-visible"
        >
          {/* Decorative only: hidden on small screens, where it would sit under
              the copy instead of beside the preview. */}
          <div
            aria-hidden="true"
            className="hero-glow absolute top-0 right-0 hidden h-[86%] overflow-hidden lg:left-[48%] lg:block"
          />

          <div className="shell shell-pad relative z-[6] grid flex-1 grid-cols-1 grid-rows-1 items-stretch lg:grid-cols-[0.95fr_1.05fr] lg:gap-10">
            {/* Pinned to the foot of the viewport on a phone: the panel takes
                the top of the screen, the headline and its buttons sit on the
                fold. `content-between` on the grid does the pushing. */}
            {/* Sits just above the protocols band rather than floating over
                it: the copy is what the band is captioning, so the gap between
                them should read as a join, not as empty space.

                Left edge matches the band below it. "Supported protocols" sits
                at pl-8 and the shell's inner padding is the same 32px, so the
                hero lines up with it by leaving that padding alone. Pulling the
                column out to the rule itself put the headline 32px left of the
                caption underneath, which reads as a mistake rather than as
                deliberate. */}
            <div className="relative z-10 col-start-1 row-start-1 flex flex-col justify-end pt-2 pb-5 lg:col-auto lg:row-auto lg:pt-10 lg:pb-0">
              {/* The panel reaches the rule; the type does not.
                  It is a backdrop — its job is to darken what the terminal is
                  doing behind the words, and stopping 32px short left a lit
                  strip down the left edge with nothing in it. So the block
                  steps out to the line and puts the padding back on the inside,
                  which keeps the headline level with "Supported protocols"
                  underneath while the dark reaches the edge. */}
              <div className="relative bg-[linear-gradient(to_top,var(--background)_0%,color-mix(in_srgb,var(--background)_72%,transparent)_55%,transparent_100%)] pt-10 backdrop-blur-[2px] lg:-ml-8 lg:w-full lg:max-w-none lg:bg-none lg:py-6 lg:pl-8 lg:backdrop-blur-none xl:w-max xl:max-w-[62vw]">
                {/* The backdrop is its own layer so the mask can fade it out
                    without taking the type with it.
                    Blur does the work, not colour: at a quarter tint the tape
                    still shows through with its greens and reds, it is simply
                    out of focus — which reads as depth, where a heavier fill
                    read as a panel laid on top. No border either: an edge drawn
                    on something whose whole point is that it has no edge.

                    Full column width until xl, then only as wide as it needs.
                    Sized to its text on a 1024px screen, the fade began part
                    way along the headline and the right edge read as clipped —
                    there is no room to be narrower than the column there. */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 hidden will-change-transform [transform:translateZ(0)] lg:block lg:bg-background/25 lg:backdrop-blur-lg lg:[mask-image:linear-gradient(to_right,black_0%,black_58%,transparent_100%)]"
                />
                <div className="relative">
              <h1 className="m-0 max-w-[640px] text-[clamp(34px,4.6vw,50px)] leading-[1.04] lg:max-w-none">
                {/* Three lines, broken here rather than left to the browser:
                    the measure and the clamped size would find their own break
                    points, and they are not these. */}
                <SplitText label="Trade Memecoins From your terminal.">
                  <span className="block lg:whitespace-nowrap">Trade</span>
                  <span className="block lg:whitespace-nowrap">Memecoins</span>
                  <span className="block text-brand lg:whitespace-nowrap">From your terminal.</span>
                </SplitText>
              </h1>
              <Reveal delay={160} className="mt-8 flex flex-wrap items-center gap-2.5">
                {/* The pair prices itself: the terminal is free, the
                    endpoint is the product. Superscripts, so the labels
                    stay labels and the numbers stay whispers. */}
                <a
                  href="#install"
                  className="inline-flex h-[41px] items-center justify-center rounded-sm bg-foreground px-8 text-[15px] font-semibold whitespace-nowrap text-background transition-colors hover:bg-brand hover:text-background"
                >
                  Install
                  <sup className="ml-1 text-[0.62em] font-medium opacity-70">free</sup>
                </a>
                {/* The price waits behind the hover: the label stays a
                    label at rest, and leaning in reveals $119/mo before the
                    arrow carries you out to the console. */}
                <a
                  href="https://rpc.trenches.sh"
                  className="group inline-flex h-[41px] items-center justify-center rounded-sm border border-brand px-8 text-[15px] font-semibold whitespace-nowrap text-brand transition-colors hover:bg-brand hover:text-background"
                >
                  Trenches RPC
                  <span className="max-w-0 overflow-hidden text-[1.15em] font-bold opacity-0 transition-all duration-300 group-hover:ml-2 group-hover:max-w-[6em] group-hover:opacity-100">
                    $119/mo
                  </span>
                  <span
                    aria-hidden="true"
                    className="max-w-0 overflow-hidden text-[1.15em] opacity-0 transition-all duration-300 group-hover:ml-2 group-hover:max-w-[1.5em] group-hover:opacity-100"
                  >
                    ↗
                  </span>
                </a>
              </Reveal>
              <p className="mt-9 max-w-[56ch] border-l-2 border-brand py-0.5 pl-4 text-[0.95rem] text-muted-foreground">
                <SplitText
                  delay={520}
                  step={7}
                  label="Start trading memecoins like a bot. Trade fast, trade like a bot — press b to buy, s to sell."
                >
                  {"Start trading memecoins like a bot. Trade fast, trade like a bot — press "}
                  <TradeKey k="b" side="buy" />
                  {" to buy, "}
                  <TradeKey k="s" side="sell" />
                  {" to sell."}
                </SplitText>
              </p>
                </div>
              </div>
            </div>

            <Reveal
              delay={120}
              // Grown to roughly eight tenths of the content measure.
              //
              // Done with the panel's OWN width and offset rather than by
              // giving its grid track a bigger share — the track also sets
              // where the copy column starts, and widening it last time just
              // moved the headline right without making the screenshot bigger.
              //
              // Retuned per breakpoint, because these are absolute consequences
              // of a relative track. `lg` starts at 1024px — an iPad Pro in
              // portrait — where the shell is only ~790px wide and the panel's
              // track ~360px, so the numbers that land the right edge just past
              // the gutter on a 2000px display pushed nearly the whole panel off
              // the screen there.
              //
              // Width has a floor set by CONTENT, not taste: the dashboard's
              // three stat boxes need room, and below it they overlap into
              // "BALANCEREALIZEDPOOLED". That is why `lg` widens rather than
              // narrows on a small screen, and the offset grows with it to keep
              // the extra width from arriving on the right.
              //
              // The two numbers work together and pull opposite ways: width
              // grows the panel, and the left offset decides whether that
              // growth eats the headline or runs off the right edge. The offset
              // is tuned to hold the LEFT edge roughly where it was, so the
              // extra width all goes rightward into space the panel already
              // bleeds into, instead of sliding over the copy.
              // Short, wide, sitting in the lower two thirds: left edge about
              // where the headline's first word ends, right edge spilling past
              // the gutter rule.
              //
              // Three numbers, and they only work together. Height is tuned
              // apart from width because the same growth applied vertically
              // pushes the panel past the fold and squashes the copy under it.
              // `self-end` plus the bottom margin is what drops it down the
              // row. The offset and the width set the two edges between them,
              // and neither can be tuned alone: the offset is a percentage of
              // the element's OWN width, so widening the panel also drags its
              // left edge further left unless the offset comes down to match.
              // Above the headline on a phone, beside it on a desktop.
              //
              // Source order puts the copy first because that is the desktop
              // layout and the reading order; in one column that would bury the
              // product under a paragraph. `order-first` moves the panel without
              // moving the markup, so the heading is still the first thing in
              // the document for anything that reads rather than looks.
              // Left edge stays put, right edge runs off. The window frame —
              // the traffic lights, the border — is what says "a desktop app",
              // and it lives on the left; bleeding both sides hid the one part
              // that carries the idea. Anchored to the content's left margin,
              // it overflows only to the right, where there is nothing to lose.
              // Nudged down so the sticky header does not shave the window's
              // top edge — the traffic lights and the frame are what make it
              // read as a desktop app, and half of one is worse than none.
              className="relative z-0 col-start-1 row-start-1 -mr-20 mb-[14vh] h-[62vh] self-end sm:-mr-24 sm:mb-[12vh] sm:h-[64vh] lg:col-auto lg:row-auto lg:mr-0 lg:mb-[7vh] lg:h-[56vh] lg:max-h-none lg:min-h-[390px] lg:w-[920px] lg:-translate-x-[276px] lg:self-end lg:pt-2 lg:pb-12 xl:mb-[8vh] xl:h-[63vh] xl:min-h-[460px] xl:w-[1080px] xl:-translate-x-[490px]"
            >
              {/* Laid out at a desktop width, then scaled down.
              
                  Letting the panel reflow into a 390px column turns it into a
                  phone app screenshot — one column, two figures, nothing that
                  says "terminal". Fixing the width at 1100px and scaling means
                  the rows, the tape and the three stat boxes keep the
                  proportions they have on a desktop, and what a phone shows is a
                  slice of that rather than a squeezed version of it. */}
              {/* Scaled about the top-left, so the box has to be 1/scale tall
                  to still cover the cell — at 0.42 in a 100%-tall box it painted
                  the top corner and left the copy sitting under it in space
                  rather than on top of it.
              
                  0.78 rather than 0.42: enough that the rows, the amounts and
                  the stat boxes are legible. Less of the 1100px layout fits, but
                  what fits can be read, and an unreadable screenshot is not
                  worth the width it saves. */}
              <div className="h-[132%] w-[1100px] origin-top-left scale-[0.78] sm:h-[122%] sm:scale-[0.85] md:h-[108%] md:scale-[0.95] lg:h-full lg:w-auto lg:scale-100">
                <div className="hero-tilt h-full">
                  <TerminalPreview float fill />
                </div>
              </div>
            </Reveal>
          </div>

            <ProtocolMarquee />
        </section>

        <FeatureTour tour={tour} />

        <LedBand
          items={[
            "START TRADING MEMECOINS LIKE A BOT",
            "TRADE FAST",
            "TRADE LIKE A BOT",
            "PRESS B TO BUY",
            "PRESS S TO SELL",
            "TRENCHES.SH",
          ]}
          label="Start trading memecoins like a bot. Trade fast, trade like a bot. Press B to buy, S to sell."
          height={120}
          dotSize={9}
        />

        <SiteFooter />
      </main>
    </div>
  );
}

export default App;
